/**
 * Webinars.tsx — v2 Webinar analytics
 *
 * Architecture:
 *  - Optins: urban_unity_webinar_optins (by assignedWebinarId) + main urban_unity_optins (date+UTM filter)
 *  - Apps/Calls/Cash: matched by email from main collections
 *  - Zoom: uploaded CSV per webinar
 *  - Paid = utm_source = "paid ads"
 *
 * Features:
 *  - Clickable funnel blocks → lead list modal → lead timeline drawer
 *  - In-funnel All/Compare toggle (organic vs paid side-by-side)
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { UU_WEBINAR_ON_WEBINAR_TARGETS, UU_WEBINAR_SALES_TARGETS, UU_WEBINAR_ZOOM_TARGETS, type UuTargetRange } from "../config/uu-webinar-card-targets";
import { SheetTable, uuSheetBenchmark } from "../components/SheetTable";
import { SheetHighlight, TRAFFIC_LABEL, TrafficSectionTitle, useTrafficSelection } from "../components/SheetHighlight";
import { VELOCITY_TARGET_ROWS, computePlan, type VelocityTargetRow } from "../config/velocity-targets";
import { buildTablePdf, targetStatus as sheetTargetStatus, type SheetExportRow } from "../lib/sheet-ui";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { motion, AnimatePresence } from "framer-motion";
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
// Explicit import prevents Bun from tree-shaking scalePoint (used internally by recharts/Brush via victory-vendor/d3-scale).
// Without this, Bun's module cache can resolve it to undefined, crashing with "Kqb is not defined".
import { scalePoint as _scalePoint } from "d3-scale";
import { branding } from "../config/branding";
import { formatWebinarRoasCashAmounts } from "../services/webinar-roas";
// One source of truth with the Webinar Projections tab (Joey, Oct 29 2026 11:05 PM ET: "When I go to the W011 webinar, they need to be
// the exact same"): the shared Sales / On-Webinar cards below take their value, label and format from lib/webinar-metrics.ts.
import { fmtWebinarMetric, webinarMetricHow, webinarMetrics, WEBINAR_METRIC_LABEL as LBL, type WebinarSegment } from "../lib/webinar-metrics";
import { WebinarAdsBreakdown } from "../components/WebinarAdsBreakdown";
import { MissingOutcomeCard } from "../components/MissingOutcomeCard"; // Calls Missing Outcome (Tab, Nov 3 2026)

// ─── Types ───────────────────────────────────────────────────────────────────

interface WebinarDef {
  id: string;
  name: string;
  date: string;
  audienceType?: "warm" | "cold";
}

// Enhanced metrics types
interface SalesMetricsBlock {
  optins: number; apps: number; fullApps: number; partialApps: number;
  bookedCalls: number; callsDue: number; takenCalls: number; noShows: number;
  cancelledBySales: number; cancelledByLead: number; rescheduled: number;
  closed: number; deposits: number;
  showRate: number; closeRate: number; cash: number; auv: number;
  cashPerCall: number; costPerBooking: number; appToBookingRate: number; bookedAfterWebinarDay?: number;
  qualifiedApps: number; qualifiedBooked: number; qualifiedToBookingRate: number;
  chargebackRate: number; roas: number; upfrontRoas: number;
}

interface OnWebinarMetrics {
  registrants: number; cpr: number; attended: number; showRate: number;
  avgDuration: number; bookingRate: number; applicationRate: number;
  pitchMinute: number | null; retentionAtPitch: number; attendeesAtPitch: number;
  appsFromAttendees: number; fullAppsFromAttendees: number;
  // Zoom's raw numbers (Zoom keeps registering people after the 6:30 PM ET opt-in cut-off) — secondary labels only.
  zoomRegistrants?: number; zoomAttended?: number; lateZoomRegistrants?: number; lateZoomAttended?: number; optinCutoff?: string;
}

/** "6:30 PM ET Oct 28" — the opt-in cut-off (Joey, Oct 29 2026 9:58 PM ET: opt-ins after 6:30 PM ET on the webinar day don't count). */
function fmtOptinCutoff(iso?: string): string {
  if (!iso) return "6:30 PM ET cut-off";
  const d = new Date(iso);
  const t = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }).format(d);
  const day = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" }).format(d);
  return `${t} ET ${day}`;
}
/** Zoom raw label for the Zoom Attendance section, e.g. "Zoom: 410 incl. 45 after 6:30 PM ET Oct 28". */
function zoomRawRegLabel(ow: OnWebinarMetrics): string | undefined {
  if (ow.zoomRegistrants === undefined || ow.zoomRegistrants === ow.registrants) return undefined;
  return `Zoom registrants: ${ow.zoomRegistrants.toLocaleString("en-US")} (incl. after ${fmtOptinCutoff(ow.optinCutoff)} cut-off)`;
}
function zoomRawAttLabel(ow: OnWebinarMetrics): string | undefined {
  if (ow.zoomAttended === undefined || ow.zoomAttended === ow.attended) return undefined;
  const late = ow.lateZoomAttended ?? 0;
  return `Zoom attended: ${ow.zoomAttended.toLocaleString("en-US")}${late > 0 ? ` (${late} registered after cut-off)` : ""}`;
}

interface LowTicketMetrics {
  purchasers: number; purchaseRate: number; liquidationPct: number; auv: number;
  purchaserWebinarShowRate: number; purchaserToBookingRate: number;
  purchaserToShowRate: number; purchaserToCloseRate: number; totalRevenue: number;
}

interface DialMetrics {
  totalDials: number; pickupRate: number; callsBooked: number;
}

interface EnhancedWebinarData {
  webinarId: string; webinarName: string; webinarDate: string;
  adSpend: number; hasZoomData: boolean; landingPageVisitors: number | null;
  /** Paid view only: opt-ins / landing page visitors read from Meta Ads Manager (leads & landing page views of the webinar campaigns). */
  metaPaid?: { source: string; leads: number; landingPageViews: number; linkClicks: number; namedOptins: number };
  salesTotal: SalesMetricsBlock; salesOnWebby: SalesMetricsBlock; salesPostWebby: SalesMetricsBlock;
  onWebinar: OnWebinarMetrics; lowTicket: LowTicketMetrics; dials: DialMetrics;
  webinarStartTime: string | null; webinarEndTime: string | null;
  totalEocsDue: number; totalEocsTaken: number;
  callShowRateByDay: { dayOffset: number; label: string; eocsDue: number; eocsTaken: number; showRate: number; }[];
  addToCalendar: number; joinedWhatsApp: number;
}

interface WebinarFunnel {
  webinarId: string;
  webinarName: string;
  webinarDate: string;
  totalRegistrants: number;
  totalAttended: number;
  showRate: number;
  avgDuration: number;
  totalOptins: number;
  paidOptins: number;
  organicOptins: number;
  totalApps: number;
  paidApps: number;
  organicApps: number;
  totalCalls: number;
  paidCalls: number;
  organicCalls: number;
  totalShowed: number;
  paidShowed: number;
  organicShowed: number;
  totalWins: number;
  paidWins: number;
  organicWins: number;
  totalCash: number;
  paidCash: number;
  organicCash: number;
  optinToAppRate: number;
  appToCallRate: number;
  callShowRate: number;
  callToCloseRate: number;
  hasZoomData: boolean;
  adSpend: number;
  roas: number;
  upfrontRoas: number;
}

interface RetentionPoint {
  minute: number;
  count: number;
  pct: number;
}

interface RetentionBySourcePoint {
  minute: number;
  [source: string]: number;
}

interface CashBySourceItem {
  source: string;
  cash: number;
  wins: number;
}

interface FunnelStage {
  label: string;
  value: number;
  subLabel?: string;
  isCash?: boolean;
}

interface StageLead {
  email: string;
  name: string;
  source: string;
  outcome?: string;
  cash?: number;
  closedAt?: string;
  webinarOptinCount?: number;
}

// Lead timeline types (mirrored from Leads.tsx)
interface TimelineOptin {
  date: string; source: string; medium: string; campaign: string; content: string; sheet: string;
}
interface TimelineApplication {
  date: string; source: string; medium: string; campaign: string; content: string; responseType: string;
}
interface TimelineCall {
  date: string; outcome: string; closer: string; setter: string; cash: number; offer: string;
  notes: string; fathomLink: string; platform: string; funnel: string;
}
interface TimelineWebinarOptin {
  date: string; webinarName: string; webinarId: string; source: string; medium: string; campaign: string;
}
interface TimelineMeetingBooking {
  bookedAt: string; meetingAt: string; closer: string;
}
interface LeadTimelineData {
  email: string; name: string; phone: string; country: string; leadType: string;
  profile: { hasBrand: string; currentMRR: string; ableToInvest: string };
  optins: TimelineOptin[];
  application: TimelineApplication | null;
  calls: TimelineCall[];
  webinarOptins: TimelineWebinarOptin[];
  meetingBookings: TimelineMeetingBooking[];
  totalRevenue: number;
  isClosed: boolean;
}

// Stage label → API key mapping
const STAGE_KEY_MAP: Record<string, string> = {
  "Opt-ins": "optins",
  "Registrants": "registrants",
  "Attended": "attended",
  "Applications": "apps",
  "Total Apps": "apps",
  "Full Apps": "full-apps",
  "Qualified Apps": "qualified-apps",
  "Partial Apps": "partial-apps",
  "Calls Booked": "calls",
  "Webinar Bookings": "calls",
  "Calls Due (EOCs)": "eocs-due",
  "End of Call Reports Filled": "eocs-due",
  "Calls Taken": "showed",
  "No Shows": "no-shows",
  "Deposits": "deposits",
  "Closed": "wins",
  "Closes": "wins",
  "Cash": "wins",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt$ = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtPct = (n: number) => `${n.toFixed(1)}%`;
const fmtN = (n: number) => Math.round(n).toLocaleString();

const formatDate = (s: string) => {
  const d = new Date(s + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};

function fmtDate(iso: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── Target Ranges ───────────────────────────────────────────────────────────
// Urban Unity's own targets (Jeremy Haynes AI's $200K/mo model on UU's last 6 months), from
// config/velocity-targets.ts via config/uu-webinar-card-targets.ts — never Cr8Her's benchmarks (Joey, Oct 29 2026).
// Volume targets are per webinar = one week of the webinar funnel; rates/averages are levels; text ranges stay ranges.
type TargetRange = UuTargetRange;

const SALES_TARGETS: Record<string, { cold?: TargetRange; warm?: TargetRange }> = UU_WEBINAR_SALES_TARGETS;
const ON_WEBINAR_TARGETS: Record<string, { cold?: TargetRange; warm?: TargetRange }> = UU_WEBINAR_ON_WEBINAR_TARGETS;
const ZOOM_TARGETS: Record<string, { cold?: TargetRange; warm?: TargetRange }> = UU_WEBINAR_ZOOM_TARGETS;

/** Display string ("288+", "≤2", "20–30%") and indicator for a UU target. above = better than target. */
function targetDisplay(t: TargetRange, val: number): { rangeStr: string; indicator: "above" | "in" | "below" } {
  const fmtV = t.unit === "$" ? (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K` : `$${Math.round(n)}`
    : t.unit === "min" ? (n: number) => `${Math.round(n)}m`
    : t.unit === "n" ? (n: number) => n < 10 && !Number.isInteger(n) ? n.toFixed(1) : `${Math.round(n)}`
    : (n: number) => `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
  if (t.dir === "range") {
    const lo = t.unit === "%" ? `${Number.isInteger(t.lo) ? t.lo : t.lo.toFixed(1)}` : fmtV(t.lo);
    return { rangeStr: `${lo}–${fmtV(t.hi)}`, indicator: val > t.hi ? "above" : val >= t.lo ? "in" : "below" };
  }
  if (t.dir === "max") return { rangeStr: `≤${fmtV(t.hi)}`, indicator: val <= t.hi ? "above" : "below" };
  return { rangeStr: `${fmtV(t.lo)}+`, indicator: val >= t.lo * 1.0001 ? "above" : val >= t.lo * 0.85 ? "in" : "below" };
}

function resolveTarget(label: string, audienceType: "warm" | "cold" | undefined, isOnWebinar = false): TargetRange | null {
  const map = isOnWebinar ? ON_WEBINAR_TARGETS : SALES_TARGETS;
  const entry = map[label];
  if (!entry) return null;
  // UU's targets are the same for warm and cold traffic (one plan), so the audience type no longer changes them.
  void audienceType;
  return entry.warm ?? entry.cold ?? null;
}

function renderTargetBadge(val: number, target: TargetRange | null, isCompare: boolean, _targetBase = 0): React.ReactNode {
  if (isCompare || !target || !Number.isFinite(val)) return null;
  const { rangeStr, indicator } = targetDisplay(target, val);
  const iconColor = indicator === "above" ? "#34d399" : indicator === "below" ? "#f87171" : "#facc15";
  const textColor = indicator === "above" ? "text-emerald-400" : indicator === "below" ? "text-red-400" : "text-yellow-400";
  const icon = indicator === "above" ? (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2,11 5.5,7 8.5,9 12,3" /><polyline points="9,3 12,3 12,6" />
    </svg>
  ) : indicator === "below" ? (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="2,3 5.5,7 8.5,5 12,11" /><polyline points="9,11 12,11 12,8" />
    </svg>
  ) : (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke={iconColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5.5" /><polyline points="4.5,7.5 6.5,9.5 9.5,5" />
    </svg>
  );
  return (
    <div className={`flex items-center gap-1.5 mt-2 ${textColor}`}>
      {icon}
      <span className="text-[10px] font-semibold leading-tight">target {rangeStr}</span>
    </div>
  );
}

function renderMetricDelta(current: number, prev: number, lowerIsBetter = false): React.ReactNode {
  if (prev === 0 || current === 0) return null;
  const delta = current - prev;
  const pct = Math.abs((delta / prev) * 100);
  if (pct < 0.1) return null;
  const isPositive = lowerIsBetter ? delta < 0 : delta > 0;
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${isPositive ? "bg-green-500/15 text-green-400" : "bg-red-500/15 text-red-400"}`}>
      {isPositive ? "↗" : "↘"} {delta > 0 ? "+" : ""}{pct.toFixed(1)}% vs prev
    </span>
  );
}


// ─── All-time blended export (Cr8Her's "All-time blended ↓ CSV / Excel / PDF", on UU's webinar service) ─────
type BlendedWebinarExportRow = { group: string; metric: string; value: string; notes?: string };

export function blendedWebinarExportRows(webinars: WebinarFunnel[]): BlendedWebinarExportRow[] {
  const total = (key: keyof WebinarFunnel) => webinars.reduce((sum, webinar) => sum + (Number(webinar[key]) || 0), 0);
  const registrants = total("totalRegistrants"); const attended = total("totalAttended");
  const optins = total("totalOptins"); const apps = total("totalApps"); const calls = total("totalCalls");
  const showed = total("totalShowed"); const wins = total("totalWins"); const cash = total("totalCash");
  const adSpend = total("adSpend");
  const avgDuration = attended > 0 ? webinars.reduce((sum, webinar) => sum + (webinar.avgDuration || 0) * (webinar.totalAttended || 0), 0) / attended : 0;
  const rate = (numerator: number, denominator: number) => denominator > 0 ? fmtPct((numerator / denominator) * 100) : "N/A";
  const money = (value: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);
  return [
    { group: "Scope", metric: "Webinars Included", value: fmtN(webinars.length), notes: "All saved webinars, all time" },
    { group: "Registration & attendance", metric: "Registrants", value: fmtN(optins), notes: `Opt-ins up to 6:30 PM ET on each webinar day (Zoom registrants incl. later sign-ups: ${fmtN(registrants)})` },
    { group: "Registration & attendance", metric: "Attended", value: fmtN(attended), notes: "Zoom attendees who registered before the 6:30 PM ET cut-off" },
    { group: "Registration & attendance", metric: "Webinar Show Rate", value: rate(attended, optins), notes: `${fmtN(attended)} attended / ${fmtN(optins)} opt-ins` },
    { group: "Registration & attendance", metric: "Average Attendance Duration", value: attended > 0 ? `${Math.round(avgDuration)}m` : "N/A", notes: "Weighted by attended records" },
    { group: "Acquisition", metric: "Opt-ins", value: fmtN(optins) },
    { group: "Acquisition", metric: "Paid Opt-ins", value: fmtN(total("paidOptins")) },
    { group: "Acquisition", metric: "Organic Opt-ins", value: fmtN(total("organicOptins")) },
    { group: "Funnel", metric: "Total Applications", value: fmtN(apps) },
    { group: "Funnel", metric: "Opt-in → Application Rate", value: rate(apps, optins), notes: `${fmtN(apps)} applications / ${fmtN(optins)} opt-ins` },
    { group: "Funnel", metric: "Calls Booked", value: fmtN(calls), notes: "Webinar-attributed bookings" },
    { group: "Funnel", metric: "Application → Call Rate", value: rate(calls, apps), notes: `${fmtN(calls)} calls / ${fmtN(apps)} applications` },
    { group: "Sales", metric: "Calls Taken", value: fmtN(showed) },
    { group: "Sales", metric: "Call Show Rate", value: rate(showed, calls), notes: `${fmtN(showed)} taken / ${fmtN(calls)} booked` },
    { group: "Sales", metric: "Closed Deals", value: fmtN(wins) },
    { group: "Sales", metric: "Close Rate", value: rate(wins, showed), notes: `${fmtN(wins)} closed / ${fmtN(showed)} taken` },
    { group: "Sales", metric: "Cash Collected", value: money(cash) },
    { group: "Sales", metric: "Average Cash per Close", value: wins > 0 ? money(cash / wins) : "N/A" },
    { group: "Efficiency", metric: "Ad Spend", value: money(adSpend) },
    { group: "Efficiency", metric: "ROAS", value: adSpend > 0 ? `${(cash / adSpend).toFixed(2)}x` : "N/A", notes: adSpend > 0 ? `${money(cash)} cash / ${money(adSpend)} spend` : "No recorded ad spend" },
  ];
}

function downloadExportBlob(blob: Blob, filename: string) {
  const link = document.createElement("a"); link.href = URL.createObjectURL(blob); link.download = filename; link.click(); URL.revokeObjectURL(link.href);
}

function webinarPdfText(value: string) { return value.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)").replace(/[–—→]/g, "-"); }

export function buildBlendedWebinarPdf(rows: BlendedWebinarExportRow[]) {
  const pageWidth = 792; const pageHeight = 612; const left = 48; const right = 48; const headerY = 518; const rowHeight = 18;
  const text = (x: number, y: number, value: string, size = 9, bold = false, color = "0.12 0.12 0.14") => `BT /F${bold ? "2" : "1"} ${size} Tf ${color} rg 1 0 0 1 ${x} ${y} Tm (${webinarPdfText(value)}) Tj ET`;
  const pages: string[] = [];
  for (let offset = 0; offset < rows.length; offset += 22) {
    const pageRows = rows.slice(offset, offset + 22); const continued = offset > 0;
    const commands = [text(left, 570, "Urban Unity Webinar Metrics Export", 16, true), text(left, 551, "Blended metrics across all saved webinars · All time", 9, false, "0.32 0.35 0.4"), text(left, 530, `All Webinar Metrics (Blended)${continued ? " (continued)" : ""}`, 11, true, "0.23 0.31 0.44"), "0.23 0.31 0.44 rg", `${left} ${headerY} ${pageWidth - left - right} 22 re f`, text(left + 8, headerY + 7, "Metric", 8, true, "1 1 1"), text(410, headerY + 7, "Value", 8, true, "1 1 1"), text(530, headerY + 7, "Calculation", 8, true, "1 1 1")];
    pageRows.forEach((row, index) => { const y = headerY - 22 - index * rowHeight; if (index === 0 || row.group !== pageRows[index - 1].group) commands.push(text(left + 8, y - 4, row.group.toUpperCase(), 7, true, "0.35 0.37 0.42")); commands.push(text(left + 150, y - 4, row.metric, 8), text(410, y - 4, row.value, 8, true), text(530, y - 4, row.notes || "", 7, false, "0.35 0.37 0.42"), `0.82 G 0.35 w ${left} ${y - rowHeight} m ${pageWidth - right} ${y - rowHeight} l S`); });
    commands.push(text(left, 42, "Sources: Urban Unity webinar service (attendance, acquisition, webinar-attributed calls, closes and cash)", 7, false, "0.35 0.37 0.4")); pages.push(commands.join("\n"));
  }
  const objects: string[] = ["<< /Type /Catalog /Pages 2 0 R >>", ""]; const pageNumbers = pages.map((_, index) => 3 + index * 2); objects[1] = `<< /Type /Pages /Kids [${pageNumbers.map((n) => `${n} 0 R`).join(" ")}] /Count ${pages.length} >>`;
  pages.forEach((content, index) => { const page = 3 + index * 2; objects[page - 1] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${pages.length * 2 + 3} 0 R /F2 ${pages.length * 2 + 4} 0 R >> >> /Contents ${page + 1} 0 R >>`; objects[page] = `<< /Length ${content.length} >>\nstream\n${content}\nendstream`; });
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>", "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>"); let pdf = "%PDF-1.4\n"; const offsets = [0]; objects.forEach((object, index) => { offsets.push(pdf.length); pdf += `${index + 1} 0 obj\n${object}\nendobj\n`; }); const xref = pdf.length; pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`; offsets.slice(1).forEach((value) => { pdf += `${String(value).padStart(10, "0")} 00000 n \n`; }); pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`; return new Blob([pdf], { type: "application/pdf" });
}

// ─── OutcomeBadge ─────────────────────────────────────────────────────────────

function OutcomeBadge({ outcome }: { outcome: string }) {
  const closed = new Set(["Closed", "closed", "Close", "close", "Won", "won", "Sale", "sale"]);
  const noshow = new Set(["No Show", "Cancelled", "Rescheduled", "Cancelled by sales team"]);
  const isWon = closed.has(outcome);
  const isNoShow = noshow.has(outcome) || !outcome;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
      isWon ? "bg-emerald-500/15 text-emerald-400" :
      isNoShow ? "bg-zinc-500/15 text-zinc-400" :
      "bg-amber-500/15 text-amber-400"
    }`}>
      {outcome || "No Show"}
    </span>
  );
}

// ─── Lead Timeline Drawer ─────────────────────────────────────────────────────

function LeadTimelineDrawer({ email, onClose }: { email: string; onClose: () => void }) {
  const [data, setData] = useState<LeadTimelineData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setData(null);
    fetch(`/api/lead-timeline?email=${encodeURIComponent(email)}`)
      .then((r) => r.json() as Promise<LeadTimelineData>)
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [email]);

  const pri = branding.colors.primary;
  const sec = branding.colors.secondary;

  const events = data ? [
    ...data.optins.map((o) => ({ type: "optin" as const, date: o.date, data: o })),
    ...(data.webinarOptins ?? []).map((w) => ({ type: "webinar" as const, date: w.date, data: w })),
    ...(data.meetingBookings ?? []).map((m) => ({ type: "meeting" as const, date: m.bookedAt, data: m })),
    ...(data.application ? [{ type: "app" as const, date: data.application.date, data: data.application }] : []),
    ...data.calls.map((c) => ({ type: "call" as const, date: c.date, data: c })),
  ].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return -1;
    if (!b.date) return 1;
    return a.date.localeCompare(b.date);
  }) : [];

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="fixed right-0 top-0 h-full w-full max-w-[520px] bg-card border-l border-border z-[70] flex flex-col overflow-hidden"
        style={{ boxShadow: "-8px 0 40px rgba(0,0,0,0.2)" }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-bold text-foreground truncate">{data?.name || email}</h2>
              {data?.isClosed && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />Closed Won
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{email}</p>
            {data && (data.phone || data.country) && (
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {[data.phone, data.country].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          <button onClick={onClose}
            className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/60 text-muted-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* KPI strip */}
        {data && (
          <div className="flex gap-3 px-5 py-3 border-b border-border shrink-0">
            {[
              { label: "Optins",   value: data.optins.length + (data.webinarOptins?.length ?? 0) },
              { label: "Applied",  value: data.application ? "Yes" : "No" },
              { label: "Meetings", value: (data.meetingBookings?.length ?? 0) + data.calls.length },
              { label: "Revenue",  value: data.totalRevenue > 0 ? `$${data.totalRevenue.toLocaleString()}` : "$0" },
            ].map((k) => (
              <div key={k.label} className="flex-1 min-w-0 rounded-xl px-3 py-2 text-center"
                style={{
                  background: `rgba(${parseInt(pri.slice(1,3),16)},${parseInt(pri.slice(3,5),16)},${parseInt(pri.slice(5,7),16)},0.06)`,
                  border: `1px solid rgba(${parseInt(pri.slice(1,3),16)},${parseInt(pri.slice(3,5),16)},${parseInt(pri.slice(5,7),16)},0.12)`,
                }}>
                <div className="text-[10px] text-muted-foreground font-medium mb-0.5">{k.label}</div>
                <div className="text-[15px] font-bold text-foreground tabular-nums">{String(k.value)}</div>
              </div>
            ))}
          </div>
        )}

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-20 text-[11px] text-muted-foreground">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse mr-2" style={{ background: pri }} />
              Loading timeline…
            </div>
          )}
          {!loading && events.length === 0 && (
            <div className="flex items-center justify-center py-20 text-[11px] text-muted-foreground">
              No events found for this lead.
            </div>
          )}
          {!loading && events.length > 0 && (
            <div className="relative">
              <div className="absolute left-[19px] top-3 bottom-3 w-px bg-border" />
              <div className="space-y-5">
                {events.map((ev, i) => {
                  const isOptin   = ev.type === "optin";
                  const isApp     = ev.type === "app";
                  const isCall    = ev.type === "call";
                  const isWebinar = ev.type === "webinar";
                  const isMeeting = ev.type === "meeting";
                  const isClosed  = isCall && new Set(["Closed","closed","Close","close","Won","won","Sale","sale"]).has((ev.data as TimelineCall).outcome);
                  const dotColor  = isOptin ? sec : isApp ? pri : isWebinar ? "#f59e0b" : isMeeting ? "#8b5cf6" : isClosed ? "#10b981" : "var(--muted-foreground)";
                  const label     = isOptin ? "Opted In" : isApp ? "Submitted Application" : isWebinar ? "Webinar Registration" : isMeeting ? "Meeting Booked" : "Call";

                  return (
                    <div key={i} className="flex gap-4 relative">
                      <div className="shrink-0 w-10 flex flex-col items-center">
                        <div className="w-5 h-5 rounded-full border-2 border-background flex items-center justify-center z-10" style={{ background: dotColor }}>
                          {isOptin   && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2a5 5 0 110 10A5 5 0 0112 2zm0 12c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z"/></svg>}
                          {isApp     && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                          {isCall    && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>}
                          {isWebinar && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M15 10l4.553-2.07A1 1 0 0121 8.845v6.31a1 1 0 01-1.447.894L15 14v-4zM3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"/></svg>}
                          {isMeeting && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0 rounded-xl border border-border/60 bg-background/60 px-4 py-3 mb-1" style={{ boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}>
                        <div className="flex items-center justify-between gap-2 mb-1.5">
                          <span className="text-[11px] font-semibold text-foreground">{label}</span>
                          <span className="text-[10px] text-muted-foreground shrink-0">{ev.date ? fmtDate(ev.date) : "—"}</span>
                        </div>
                        {isOptin && (() => {
                          const o = ev.data as TimelineOptin;
                          return (
                            <div className="space-y-0.5">
                              {o.source   && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{o.source}</span></div>}
                              {o.medium   && <div className="text-[10px] text-muted-foreground">Medium: <span className="text-foreground/80">{o.medium}</span></div>}
                              {o.campaign && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{o.campaign}</span></div>}
                              {o.sheet    && <div className="text-[10px] text-muted-foreground">Sheet: <span className="text-foreground/80">{o.sheet}</span></div>}
                            </div>
                          );
                        })()}
                        {isWebinar && (() => {
                          const w = ev.data as TimelineWebinarOptin;
                          return (
                            <div className="space-y-0.5">
                              {w.webinarName && <div className="text-[10px] text-muted-foreground">Webinar: <span className="text-foreground/80">{w.webinarName}</span></div>}
                              {w.source     && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{w.source}</span></div>}
                              {w.campaign   && <div className="text-[10px] text-muted-foreground">Campaign: <span className="text-foreground/80 truncate">{w.campaign}</span></div>}
                            </div>
                          );
                        })()}
                        {isMeeting && (() => {
                          const m = ev.data as TimelineMeetingBooking;
                          return (
                            <div className="space-y-0.5">
                              {m.meetingAt && <div className="text-[10px] text-muted-foreground">Scheduled for: <span className="text-foreground/80">{fmtDate(m.meetingAt)}</span></div>}
                              {m.closer    && <div className="text-[10px] text-muted-foreground">Closer: <span className="text-foreground/80">{m.closer}</span></div>}
                            </div>
                          );
                        })()}
                        {isApp && (() => {
                          const a = ev.data as TimelineApplication;
                          return (
                            <div className="space-y-0.5">
                              {a.source       && <div className="text-[10px] text-muted-foreground">Source: <span className="text-foreground/80">{a.source}</span></div>}
                              {a.medium       && <div className="text-[10px] text-muted-foreground">Medium: <span className="text-foreground/80">{a.medium}</span></div>}
                              {a.responseType && <div className="text-[10px] text-muted-foreground">Type: <span className="text-foreground/80">{a.responseType}</span></div>}
                            </div>
                          );
                        })()}
                        {isCall && (() => {
                          const c = ev.data as TimelineCall;
                          return (
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <OutcomeBadge outcome={c.outcome} />
                                {c.cash > 0 && <span className="text-[10px] font-semibold text-emerald-400">${c.cash.toLocaleString()}</span>}
                              </div>
                              {c.offer  && <div className="text-[10px] text-muted-foreground">Offer: <span className="text-foreground/80">{c.offer}</span></div>}
                              {c.closer && <div className="text-[10px] text-muted-foreground">Closer: <span className="text-foreground/80">{c.closer}</span></div>}
                              {c.setter && <div className="text-[10px] text-muted-foreground">Setter: <span className="text-foreground/80">{c.setter}</span></div>}
                              {c.notes  && <div className="text-[10px] text-muted-foreground mt-1 italic">"{c.notes}"</div>}
                              {c.fathomLink && (
                                <a href={c.fathomLink} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[10px] mt-1"
                                  style={{ color: pri }}>
                                  View recording ↗
                                </a>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}

// ─── Retention By Source Chart (with click-to-toggle lines) ──────────────────

function RetentionBySourceChart({
  data,
  sources,
  colors,
  pitchMinute,
  sourceTotals,
}: {
  data: RetentionBySourcePoint[];
  sources: string[];
  colors: string[];
  pitchMinute?: number | null;
  sourceTotals?: Record<string, number>;
}) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  const toggle = (src: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(src)) next.delete(src);
      else next.add(src);
      return next;
    });
  };

  // Find data point closest to pitch minute for count labels
  const pitchPoint = pitchMinute != null && data.length > 0
    ? data.reduce((best, p) => Math.abs(p.minute - pitchMinute) < Math.abs(best.minute - pitchMinute) ? p : best)
    : null;

  const getCountAtPitch = (src: string): number | null => {
    if (!pitchPoint || !sourceTotals) return null;
    const pct = pitchPoint[src] as number | undefined;
    const total = sourceTotals[src];
    if (pct == null || total == null) return null;
    return Math.round(pct / 100 * total);
  };

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">Retention by traffic source</p>

      {/* Clickable legend */}
      <div className="flex flex-wrap gap-2 mb-3">
        {sources.map((src, i) => {
          const isHidden = hidden.has(src);
          const color = colors[i % colors.length];
          const count = getCountAtPitch(src);
          return (
            <button
              key={src}
              onClick={() => toggle(src)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-[10px] font-medium transition-all ${
                isHidden
                  ? "border-border/30 bg-muted/20 text-muted-foreground/40 line-through"
                  : "border-border bg-muted/40 text-foreground"
              }`}
            >
              <span
                className="w-3 h-0.5 rounded-full shrink-0"
                style={{ background: isHidden ? "rgba(255,255,255,0.15)" : color }}
              />
              {src}
              {count != null && !isHidden && pitchMinute != null && (
                <span className="text-muted-foreground/60 font-normal ml-0.5">({count})</span>
              )}
            </button>
          );
        })}
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          <XAxis dataKey="minute" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${v}m`} />
          <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${Math.round(v as number)}%`} domain={[0, 100]} />
          <Tooltip
            contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
            labelFormatter={(v) => `${v} min`}
            formatter={(v: number, name: string) => {
              if (hidden.has(name)) return [];
              const total = sourceTotals?.[name];
              const count = total != null ? Math.round(v / 100 * total) : null;
              return [count != null ? `${v.toFixed(1)}% (${count} people)` : `${v.toFixed(1)}%`, name];
            }}
          />
          {pitchMinute != null && (
            <ReferenceLine x={pitchMinute} stroke="rgba(255,255,255,0.35)" strokeDasharray="4 3"
              label={{ value: "▼ Pitch", position: "insideTopLeft", fontSize: 9, fill: "rgba(255,255,255,0.5)", dy: -2 }} />
          )}
          {sources.map((src, i) => (
            <Line
              key={src}
              type="monotone"
              dataKey={src}
              name={src}
              stroke={colors[i % colors.length]}
              strokeWidth={src === "all" ? 2 : 1.5}
              strokeDasharray={src === "all" ? "0" : undefined}
              dot={false}
              hide={hidden.has(src)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Cash By Source Pie Chart ────────────────────────────────────────────────

function CashBySourceChart({ data, title = "Cash Collected by Traffic Source/Creator" }: { data: CashBySourceItem[]; title?: string }) {
  const colors = branding.colors.chart;
  const total = data.reduce((s, d) => s + d.cash, 0);

  if (data.length === 0) {
    return (
      <div>
        <p className="text-xs text-muted-foreground mb-3">{title}</p>
        <div className="flex items-center justify-center py-4">
          <svg width="140" height="140" viewBox="0 0 140 140">
            <circle cx="70" cy="70" r="62" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="16" />
            <text x="70" y="65" textAnchor="middle" dominantBaseline="middle" fill="rgba(255,255,255,0.22)" fontSize="22" fontWeight="700">$0</text>
            <text x="70" y="85" textAnchor="middle" dominantBaseline="middle" fill="rgba(255,255,255,0.12)" fontSize="11">no data</text>
          </svg>
        </div>
      </div>
    );
  }

  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: {
    cx: number; cy: number; midAngle: number; innerRadius: number; outerRadius: number; percent: number;
  }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">{title}</p>
      <div className="flex flex-col md:flex-row items-center gap-6">
        <ResponsiveContainer width={280} height={280}>
          <PieChart>
            <Pie
              data={data}
              dataKey="cash"
              nameKey="source"
              cx="50%"
              cy="50%"
              outerRadius={115}
              labelLine={false}
              label={renderLabel}
            >
              {data.map((entry, i) => (
                <Cell key={entry.source} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              formatter={(v: number, name: string) => [`$${v.toLocaleString()}`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-col gap-3 min-w-0">
          {data.map((entry, i) => (
            <div key={entry.source} className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colors[i % colors.length] }} />
              <span className="text-sm text-muted-foreground min-w-[90px]">{entry.source}</span>
              <span className="text-sm font-bold text-foreground">${entry.cash.toLocaleString()}</span>
              <span className="text-xs font-bold text-white bg-white/10 border border-white/15 rounded-md px-1.5 py-0.5 tabular-nums">
                {total > 0 ? `${((entry.cash / total) * 100).toFixed(0)}%` : ""}
              </span>
              <span className="text-xs text-muted-foreground/40">{entry.wins}w</span>
            </div>
          ))}
          <div className="mt-1 pt-2 border-t border-border/20 flex items-center gap-3">
            <span className="text-sm font-bold text-foreground">${total.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground/50">total</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Funnel Chart (inverted triangle, clickable) ─────────────────────────────

function FunnelChart({
  stages,
  gradientId = "fgA",
  onStageClick,
  mutedValues = false,
}: {
  stages: FunnelStage[];
  gradientId?: string;
  onStageClick?: (label: string) => void;
  mutedValues?: boolean;
}) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const valueFill = mutedValues ? "rgba(156,163,175,0.95)" : "rgba(255,255,255,0.95)";
  const subValueFill = mutedValues ? "rgba(156,163,175,0.75)" : "rgba(255,255,255,0.6)";

  const FULL_W = 370;
  const STAGE_H = 58;
  const TIP_W = 40;
  const GAP = 2;
  const LABEL_PAD = 130;
  const CONV_PAD = 126;
  const totalW = FULL_W + LABEL_PAD + CONV_PAD;
  const totalH = stages.length * (STAGE_H + GAP);

  // Fixed triangle shape — evenly stepped from full width down to a point, ignoring data values
  const n = stages.length;
  const stageWidths = stages.map((_, i) => {
    const t = n <= 1 ? 1 : i / (n - 1);
    return Math.round(FULL_W - t * (FULL_W - TIP_W));
  });

  return (
    <svg
      viewBox={`0 0 ${totalW} ${totalH}`}
      className="w-full"
      style={{ height: Math.min(totalH, 460) }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={branding.colors.gradient.start} stopOpacity={0.92} />
          <stop offset="100%" stopColor={branding.colors.gradient.end} stopOpacity={0.92} />
        </linearGradient>
      </defs>

      {stages.map((stage, i) => {
        const topW    = stageWidths[i];
        const bottomW = i < stages.length - 1 ? stageWidths[i + 1] : 0;
        const funnelLeft  = LABEL_PAD;
        const topLeft     = funnelLeft + (FULL_W - topW) / 2;
        const topRight    = funnelLeft + (FULL_W + topW) / 2;
        const bottomLeft  = funnelLeft + (FULL_W - bottomW) / 2;
        const bottomRight = funnelLeft + (FULL_W + bottomW) / 2;
        const y  = i * (STAGE_H + GAP);
        const cy = y + STAGE_H / 2;
        const pts = `${topLeft},${y} ${topRight},${y} ${bottomRight},${y + STAGE_H} ${bottomLeft},${y + STAGE_H}`;

        const convPct =
          i > 0 && i < stages.length - 1 && stages[i - 1].value > 0
            ? `${Math.round((stage.value / stages[i - 1].value) * 100)}%`
            : null;

        const opacity  = Math.max(0.5, 1 - i * 0.055);
        const isHovered = hoveredIdx === i;

        return (
          <g
            key={stage.label}
            onClick={() => onStageClick?.(stage.label)}
            onMouseEnter={() => onStageClick && setHoveredIdx(i)}
            onMouseLeave={() => setHoveredIdx(null)}
            style={{ cursor: onStageClick ? "pointer" : "default" }}
          >
            <polygon points={pts} fill={`url(#${gradientId})`} opacity={opacity} />

            {/* Hover overlay */}
            {isHovered && <polygon points={pts} fill="white" opacity={0.1} />}

            {/* Stage label — follows the left funnel edge inward */}
            <text
              x={topLeft - 8} y={cy}
              textAnchor="end" dominantBaseline="middle"
              fontSize={11} fontWeight="600" fontFamily="sans-serif"
              fill="rgba(255,255,255,0.82)"
            >
              {stage.label}
            </text>

            {/* Primary value — follows the right funnel edge inward */}
            <text
              x={topRight + 8}
              y={stage.subLabel ? cy - 7 : cy}
              textAnchor="start" dominantBaseline="middle"
              fontSize={13} fontWeight="bold" fontFamily="sans-serif"
              fill={valueFill}
            >
              {stage.isCash ? fmt$(stage.value) : fmtN(stage.value)}
            </text>

            {stage.subLabel && (
              <text
                x={topRight + 8} y={cy + 9}
                textAnchor="start" dominantBaseline="middle"
                fontSize={9} fontFamily="sans-serif"
                fill={subValueFill}
              >
                {stage.subLabel}
              </text>
            )}

            {/* "click to view leads" hint on hover */}
            {isHovered && onStageClick && (
              <text
                x={LABEL_PAD + FULL_W / 2}
                y={cy}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={8} fontFamily="sans-serif"
                fill="rgba(255,255,255,0.65)"
              >
                click to view leads
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Funnel Leads Modal ───────────────────────────────────────────────────────

interface FunnelLeadsModalProps {
  webinarId: string;
  webinarName: string;
  stageLabel: string;
  stageKey: string;
  traffic: "all" | "paid" | "organic";
  onClose: () => void;
  onLeadClick: (email: string) => void;
}

function FunnelLeadsModal({
  webinarId, webinarName, stageLabel, stageKey, traffic, onClose, onLeadClick,
}: FunnelLeadsModalProps) {
  const [leads, setLeads] = useState<StageLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    setLeads([]);
    fetch(`/api/webinar2/stage-leads/${webinarId}/${stageKey}?traffic=${traffic}`)
      .then((r) => r.json())
      .then((d) => { setLeads(d.leads || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [webinarId, stageKey, traffic]);

  const filtered = search
    ? leads.filter(
        (l) =>
          l.email.toLowerCase().includes(search.toLowerCase()) ||
          l.name.toLowerCase().includes(search.toLowerCase())
      )
    : leads;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-card border border-border rounded-2xl w-full max-w-lg z-10 flex flex-col"
        style={{ maxHeight: "80vh" }}
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-sm font-bold text-foreground">{stageLabel} Leads</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {webinarName}
              {!loading && ` · ${leads.length.toLocaleString()} leads`}
              {traffic !== "all" && ` · ${traffic}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-muted/60 text-muted-foreground transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b border-border/50 shrink-0">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name…"
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-primary)]"
            autoFocus
          />
        </div>

        {/* Lead list */}
        <div className="flex-1 overflow-y-auto py-1">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
              {search ? "No leads match your search" : "No leads for this stage"}
            </div>
          ) : (
            <>
              {filtered.map((lead) => (
                <button
                  key={lead.email}
                  onClick={() => onLeadClick(lead.email)}
                  className="w-full text-left px-4 py-2.5 hover:bg-muted/30 transition-colors flex items-center justify-between gap-3 group border-b border-border/20 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">
                      {lead.name || lead.email}
                    </div>
                    {lead.name && (
                      <div className="text-[11px] text-muted-foreground truncate">{lead.email}</div>
                    )}
                    {lead.source && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">{lead.source}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {lead.webinarOptinCount != null && lead.webinarOptinCount > 0 && (
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                          lead.webinarOptinCount >= 3
                            ? "bg-[var(--brand-primary)]/20 text-[var(--brand-primary)]"
                            : "bg-muted text-muted-foreground"
                        }`}
                        title={`${lead.webinarOptinCount} webinar optin${lead.webinarOptinCount !== 1 ? "s" : ""}`}
                      >
                        ×{lead.webinarOptinCount}
                      </span>
                    )}
                    {lead.closedAt && (
                      <span className="text-[10px] text-muted-foreground">{lead.closedAt}</span>
                    )}
                    {lead.outcome && <OutcomeBadge outcome={lead.outcome} />}
                    {lead.cash != null && lead.cash > 0 && (
                      <span className="text-xs font-semibold text-emerald-400">
                        ${lead.cash.toLocaleString()}
                      </span>
                    )}
                    <svg className="w-4 h-4 text-muted-foreground/50 group-hover:text-foreground transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
              {leads.length >= 500 && search === "" && (
                <p className="text-center py-3 text-[10px] text-muted-foreground">
                  Showing first 500 leads · use search to find specific leads
                </p>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Zoom Upload Modal ────────────────────────────────────────────────────────

interface UploadModalProps {
  webinarId: string;
  webinarName: string;
  onClose: () => void;
  onSuccess: (count: number) => void;
}

function ZoomUploadModal({ webinarId, webinarName, onClose, onSuccess }: UploadModalProps) {
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<"idle" | "uploading" | "success" | "error">("idle");
  const [result, setResult] = useState<{ imported: number; attended: number } | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (f: File) => {
    if (!f.name.endsWith(".csv")) { setErrorMsg("Please upload a CSV file."); return; }
    setFile(f);
    setErrorMsg("");
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setStatus("uploading");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`/api/webinar2/zoom-upload/${webinarId}`, { method: "POST", body: fd });
      const data = await res.json();
      if (data.success) {
        setResult({ imported: data.imported, attended: data.attended });
        setStatus("success");
        onSuccess(data.imported);
      } else {
        setErrorMsg(data.error || "Upload failed");
        setStatus("error");
      }
    } catch {
      setErrorMsg("Network error");
      setStatus("error");
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md z-10"
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-bold text-foreground">Upload Zoom Report</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{webinarName}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none">&times;</button>
        </div>

        {status === "success" ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">✓</div>
            <p className="text-foreground font-semibold">Upload complete</p>
            <p className="text-muted-foreground text-sm mt-1">
              {result?.imported} registrants imported · {result?.attended} attended
            </p>
            <button onClick={onClose} className="mt-4 px-5 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium">
              Done
            </button>
          </div>
        ) : (
          <>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                dragging ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/5" : "border-border hover:border-border/70"
              }`}
            >
              <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
              <div className="text-2xl mb-2">📂</div>
              {file ? (
                <p className="text-sm text-foreground font-medium">{file.name}</p>
              ) : (
                <>
                  <p className="text-sm text-foreground font-medium">Drop CSV or click to browse</p>
                  <p className="text-xs text-muted-foreground mt-1">Zoom attendee/registrant report</p>
                </>
              )}
            </div>
            {errorMsg && <p className="text-red-400 text-xs mt-2">{errorMsg}</p>}
            <p className="text-xs text-muted-foreground mt-3">
              Required columns: Attended, Email, First Name, Last Name, Time in Session (minutes), Registration Time, Join Time, Leave Time
            </p>
            <div className="flex gap-2 mt-4">
              <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!file || status === "uploading"}
                className="flex-1 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium disabled:opacity-50 transition-opacity"
              >
                {status === "uploading" ? "Uploading…" : "Upload"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Add Webinar Modal ────────────────────────────────────────────────────────

// ─── Opt-ins by UTM Pie Chart ─────────────────────────────────────────────────
function OptinsByUtmPieChart({ data, title }: { data: { name: string; count: number }[]; title: string }) {
  const colors = branding.colors.chart;
  const total = data.reduce((s, d) => s + d.count, 0);

  const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: {
    cx: number; cy: number; midAngle: number; innerRadius: number; outerRadius: number; percent: number;
  }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={11} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <div>
      <p className="text-xs text-muted-foreground mb-3">{title}</p>
      <div className="flex flex-col md:flex-row items-center gap-6">
        <ResponsiveContainer width={280} height={280}>
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={115} labelLine={false} label={renderLabel}>
              {data.map((entry, i) => (
                <Cell key={entry.name} fill={colors[i % colors.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
              formatter={(v: number, name: string) => [`${v.toLocaleString()} opt-ins`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="flex flex-col gap-3 min-w-0">
          {data.map((entry, i) => (
            <div key={entry.name} className="flex items-center gap-3">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: colors[i % colors.length] }} />
              <span className="text-sm text-muted-foreground min-w-[90px] truncate max-w-[160px]" title={entry.name}>{entry.name}</span>
              <span className="text-sm font-bold text-foreground">{entry.count.toLocaleString()}</span>
              <span className="text-xs font-bold text-white bg-white/10 border border-white/15 rounded-md px-1.5 py-0.5 tabular-nums">
                {total > 0 ? `${((entry.count / total) * 100).toFixed(0)}%` : ""}
              </span>
            </div>
          ))}
          <div className="mt-1 pt-2 border-t border-border/20 flex items-center gap-3">
            <span className="text-sm font-bold text-foreground">{total.toLocaleString()}</span>
            <span className="text-xs text-muted-foreground/50">total</span>
          </div>
        </div>
      </div>
    </div>
  );
}

interface AddWebinarModalProps {
  onClose: () => void;
  onSuccess: (webinarId: string) => void;
}

function AddWebinarModal({ onClose, onSuccess }: AddWebinarModalProps) {
  const [date, setDate] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [detectedDate, setDetectedDate] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ webinarId: string; imported: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // New fields
  const [audienceType, setAudienceType] = useState<"" | "warm" | "cold">("");
  const [mktStartDate, setMktStartDate] = useState("");
  const [mktStartTime, setMktStartTime] = useState("");
  const [mktEndDate, setMktEndDate] = useState("");
  const [mktEndTime, setMktEndTime] = useState("");
  const [pitchTime, setPitchTime] = useState("");
  const [webinarEndTime, setWebinarEndTime] = useState("");

  // Marketing window = whole America/New_York days unless a time is typed (Tab, Oct 29 2026: W011 had been saved as
  // Oct 24 12:00 PM → Oct 28 6:30 PM ET, dropping 16 paid opt-ins). A blank time sends the bare date and the server stores
  // 00:00:00 ET (start) / 23:59:59.999 ET (end); a typed time is read as ET wall time whatever the browser's time zone.
  const toET = (dateStr: string, timeStr: string): string => {
    if (!dateStr) return "";
    if (!timeStr) return dateStr;
    const [y, mo, d] = dateStr.split("-").map(Number) as [number, number, number];
    const [hh, mm] = timeStr.split(":").map(Number) as [number, number];
    const wall = Date.UTC(y, mo - 1, d, hh, mm);
    const offName = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", timeZoneName: "shortOffset" }).formatToParts(new Date(wall + 5 * 3600_000)).find((p) => p.type === "timeZoneName")?.value ?? "GMT-5";
    const m = offName.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    const offMin = m ? (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] ?? 0)) : -300;
    return new Date(wall - offMin * 60_000).toISOString();
  };

  // Sniff date from CSV header client-side so user sees it before submitting
  const handleFileChange = (f: File) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split(/\r?\n/).slice(0, 6);
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].toLowerCase().includes("topic") && i + 1 < lines.length) {
          const headerCols = lines[i].split(",").map((h) => h.trim().toLowerCase());
          const dataRow = lines[i + 1].split(",").map((v) => v.trim().replace(/^"|"$/g, ""));
          const startIdx = headerCols.findIndex((h) => h.includes("actual start time") || h.includes("start time"));
          if (startIdx >= 0 && dataRow[startIdx]) {
            const d = new Date(dataRow[startIdx]);
            if (!isNaN(d.getTime())) {
              const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              setDetectedDate(iso);
              if (!date) setDate(iso);
            }
          }
          break;
        }
      }
    };
    reader.readAsText(f);
  };

  const handleSubmit = async () => {
    const effectiveDate = date || detectedDate;
    if (!effectiveDate && !file) { setError("Upload a Zoom CSV or enter a date"); return; }
    setStatus("loading");
    setError("");
    try {
      const fd = new FormData();
      if (effectiveDate) fd.append("date", effectiveDate);
      if (file) fd.append("file", file);
      if (audienceType) fd.append("audienceType", audienceType);
      const ms = toET(mktStartDate, mktStartTime);
      const me = toET(mktEndDate, mktEndTime);
      if (ms) fd.append("marketingStart", ms);
      if (me) fd.append("marketingEnd", me);
      if (pitchTime) fd.append("pitchStartTime", new Date(pitchTime).toISOString());
      if (webinarEndTime) fd.append("webinarEndTime", new Date(webinarEndTime).toISOString());
      const res = await fetch("/api/webinar2/create", { method: "POST", body: fd });
      const data = await res.json() as { success: boolean; webinarId?: string; imported?: number; error?: string };
      if (data.success && data.webinarId) {
        setResult({ webinarId: data.webinarId, imported: data.imported ?? 0 });
        setStatus("success");
      } else {
        setError(data.error ?? "Failed to create webinar");
        setStatus("error");
      }
    } catch {
      setError("Network error");
      setStatus("error");
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative bg-card border border-border rounded-2xl p-6 w-full max-w-md z-10"
        initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-bold text-foreground">Add New Webinar</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Upload the Zoom attendee report — date auto-detected</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none">&times;</button>
        </div>

        {status === "success" && result ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">✓</div>
            <p className="text-foreground font-semibold">{result.webinarId} created</p>
            {result.imported > 0 && (
              <p className="text-muted-foreground text-sm mt-1">{result.imported} unique attendees imported</p>
            )}
            <button
              onClick={() => onSuccess(result!.webinarId)}
              className="mt-4 px-5 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium"
            >
              View Webinar
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
              {/* CSV upload */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">
                  Zoom Attendee Report CSV <span className="text-[var(--brand-primary)]">*</span>
                </label>
                <div onClick={() => inputRef.current?.click()}
                  className="border border-dashed border-border rounded-xl p-4 text-center cursor-pointer hover:border-[var(--brand-primary)]/50 transition-colors">
                  <input ref={inputRef} type="file" accept=".csv" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileChange(f); }} />
                  {file ? (
                    <div>
                      <p className="text-sm text-foreground">{file.name}</p>
                      {detectedDate && <p className="text-xs text-[var(--brand-primary)] mt-1">Date detected: {detectedDate}</p>}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Click to upload — date/start time will be auto-detected</p>
                  )}
                </div>
              </div>

              {/* Manual date override */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">
                  Webinar Date <span className="text-muted-foreground/60">(override if needed)</span>
                </label>
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
              </div>

              {/* Audience Type */}
              <div>
                <label className="text-xs text-muted-foreground block mb-1.5">Audience Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {(["", "warm", "cold"] as const).map((t) => (
                    <button key={t} type="button" onClick={() => setAudienceType(t)}
                      className={`py-2 rounded-lg border text-sm font-medium transition-colors ${audienceType === t ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]" : "border-border text-muted-foreground hover:text-foreground"}`}>
                      {t === "" ? "Unknown" : t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Attribution Timing note */}
              <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-foreground">Attribution Timing</span>
                  <span className="text-[10px] text-muted-foreground bg-muted/40 px-1.5 py-0.5 rounded">ET</span>
                </div>
                <p className="text-[11px] text-muted-foreground">All times are interpreted as Eastern time. Click any date or time field to use the browser/native picker.</p>
              </div>

              {/* Marketing Window */}
              <div className="rounded-xl border border-border/50 bg-muted/20 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold text-foreground">Marketing Window</span>
                    <p className="text-[11px] text-muted-foreground">Controls opt-in attribution + G4 opt-in-rate window. Leave the times blank for full days (12:00 AM start → 11:59 PM end, ET).</p>
                  </div>
                  <button type="button" onClick={() => { setMktStartDate(""); setMktStartTime(""); setMktEndDate(""); setMktEndTime(""); }}
                    className="text-[10px] text-muted-foreground hover:text-foreground">Clear</button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-muted-foreground mb-1 block">Marketing Start (ET)</label>
                    <div className="flex gap-1">
                      <input type="date" value={mktStartDate} onChange={(e) => setMktStartDate(e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                      <input type="time" value={mktStartTime} onChange={(e) => setMktStartTime(e.target.value)}
                        className="w-24 px-2 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground mb-1 block">Marketing End (ET)</label>
                    <div className="flex gap-1">
                      <input type="date" value={mktEndDate} onChange={(e) => setMktEndDate(e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                      <input type="time" value={mktEndTime} onChange={(e) => setMktEndTime(e.target.value)}
                        className="w-24 px-2 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Pitch Start + Webinar End */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-foreground">Pitch Start Time <span className="text-muted-foreground font-normal">(ET)</span></span>
                    <button type="button" onClick={() => setPitchTime("")} className="text-[10px] text-muted-foreground hover:text-foreground">Clear</button>
                  </div>
                  <input type="datetime-local" value={pitchTime} onChange={(e) => setPitchTime(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                  <p className="text-[10px] text-muted-foreground mt-1.5">Used to calculate retention at pitch from the Zoom actual start time.</p>
                </div>
                <div className="rounded-xl border border-border/50 bg-muted/20 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-foreground">Webinar End Time <span className="text-muted-foreground font-normal">(ET)</span></span>
                    <button type="button" onClick={() => setWebinarEndTime("")} className="text-[10px] text-muted-foreground hover:text-foreground">Clear</button>
                  </div>
                  <input type="datetime-local" value={webinarEndTime} onChange={(e) => setWebinarEndTime(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs bg-background border border-border rounded-lg text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                  <p className="text-[10px] text-muted-foreground mt-1.5">Controls on-webby vs post-webby application buckets.</p>
                </div>
              </div>
            </div>

            {error && <p className="text-red-400 text-xs mt-3">{error}</p>}
            <div className="flex gap-2 mt-5">
              <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
                Cancel
              </button>
              <button onClick={handleSubmit} disabled={(!date && !detectedDate && !file) || status === "loading"}
                className="flex-1 py-2 rounded-lg bg-[var(--brand-primary)] text-white text-sm font-medium disabled:opacity-50 transition-opacity">
                {status === "loading" ? "Creating…" : "Create Webinar"}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function Webinars() {
  const [webinars, setWebinars] = useState<WebinarDef[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [funnelCache, setFunnelCache] = useState<Record<string, WebinarFunnel>>({});
  const [retentionCache, setRetentionCache] = useState<Record<string, RetentionPoint[]>>({});
  const [retentionBySourceCache, setRetentionBySourceCache] = useState<Record<string, RetentionBySourcePoint[]>>({});
  const [cashBySourceCache, setCashBySourceCache] = useState<Record<string, CashBySourceItem[]>>({});
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [funnelMode, setFunnelMode] = useState<"all" | "compare">("all"); // single-view only
  const [cardTraffic, setCardTraffic] = useState<Record<string, "all" | "paid" | "organic">>({});
  const [uploadModal, setUploadModal] = useState<string | null>(null);
  const [addWebinarModal, setAddWebinarModal] = useState(false);
  const [exportingBlendedWebinars, setExportingBlendedWebinars] = useState<"csv" | "xlsx" | "pdf" | null>(null);
  const downloadBlendedWebinarExport = async (format: "csv" | "xlsx" | "pdf") => {
    setExportingBlendedWebinars(format);
    try {
      const funnelResponse = await fetch("/api/webinar2/all");
      const body = await funnelResponse.json() as { success: boolean; webinars?: WebinarFunnel[]; error?: string };
      if (!funnelResponse.ok || !body.success || !body.webinars?.length) throw new Error(body.error || "No webinar data is available for export");
      const rows = blendedWebinarExportRows(body.webinars);
      const filename = "urban-unity-all-webinar-blended-metrics";
      if (format === "pdf") {
        downloadExportBlob(buildBlendedWebinarPdf(rows), `${filename}.pdf`);
      } else {
        const matrix = [["Metric Group", "Metric", "Value", "Calculation / Notes"], ...rows.map((row) => [row.group, row.metric, row.value, row.notes || ""])];
        if (format === "csv") {
          downloadExportBlob(new Blob([matrix.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv;charset=utf-8" }), `${filename}.csv`);
        } else {
          const worksheet = XLSX.utils.aoa_to_sheet(matrix); worksheet["!cols"] = [{ wch: 28 }, { wch: 32 }, { wch: 18 }, { wch: 48 }];
          const workbook = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(workbook, worksheet, "Blended All-Time"); XLSX.writeFile(workbook, `${filename}.xlsx`);
        }
      }
    } catch (error) {
      console.error("Unable to export blended webinar metrics:", error);
      window.alert(error instanceof Error ? error.message : "Unable to export blended webinar metrics");
    } finally {
      setExportingBlendedWebinars(null);
    }
  };
  const [adSpendInput, setAdSpendInput] = useState<Record<string, string>>({});
  const [savingAdSpend, setSavingAdSpend] = useState<string | null>(null);
  const [stageLeadsModal, setStageLeadsModal] = useState<{
    webinarId: string;
    label: string;
    stageKey: string;
    traffic: "all" | "paid" | "organic";
  } | null>(null);
  const [timelineEmail, setTimelineEmail] = useState<string | null>(null);

  // Enhanced metrics state
  // Shared with the velocity sheets (Webinars → List, Book a call → Sheets, Dashboard → Velocity Metrics) for this browser
  // tab, so the "Split by traffic" pick drives the one velocity sheet too (Tab, Oct 29 2026 7:30 PM ET, Joey).
  const [trafficFilter, setTrafficFilter] = useTrafficSelection();
  const [appTypeFilter, setAppTypeFilter] = useState<"all" | "full" | "partial">("all");
  const [metricsView, setMetricsView] = useState<"total" | "on-webby" | "post-webby">("total");
  const [viewMode, setViewMode] = useState<"card" | "list">("card");
  const [enhancedData, setEnhancedData] = useState<Record<string, EnhancedWebinarData>>({});
  const [enhancedLoading, setEnhancedLoading] = useState<Set<string>>(new Set());
  const [pitchMinuteInput, setPitchMinuteInput] = useState<Record<string, string>>({});
  const [savingPitchMinute, setSavingPitchMinute] = useState<string | null>(null);
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set());
  const [hiddenOnWebinarMetrics, setHiddenOnWebinarMetrics] = useState<Set<string>>(new Set());

  const toggleMetric = (label: string) => {
    setHiddenMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const toggleOnWebinarMetric = (label: string) => {
    setHiddenOnWebinarMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const [hiddenLowTicketMetrics, setHiddenLowTicketMetrics] = useState<Set<string>>(new Set());
  // Card highlights (green/red) persisted in localStorage
  const [cardHighlights, setCardHighlights] = useState<Record<string, "green" | "red">>(() => {
    try { return JSON.parse(localStorage.getItem("uu-webinar-card-highlights") || "{}") as Record<string, "green" | "red">; } catch { return {}; }
  });
  const [regTimingCache, setRegTimingCache] = useState<Record<string, { dayOffset: number; label: string; optins: number; attended: number; showRate: number }[]>>({});
  const [optinsByUtmCache, setOptinsByUtmCache] = useState<Record<string, { byCampaign: { name: string; count: number }[]; bySource: { name: string; count: number }[]; total: number }>>({});
  const [engagementInput, setEngagementInput] = useState<Record<string, { addToCalendar: string; joinedWhatsApp: string }>>({});
  const [savingEngagement, setSavingEngagement] = useState<string | null>(null);

  const toggleCardHighlight = (webinarId: string, label: string, color: "green" | "red") => {
    setCardHighlights((prev) => {
      const key = `${webinarId}-${label}`;
      const next = { ...prev };
      if (next[key] === color) { delete next[key]; } else { next[key] = color; }
      try { localStorage.setItem("uu-webinar-card-highlights", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const clearCardHighlight = (webinarId: string, label: string) => {
    setCardHighlights((prev) => {
      const key = `${webinarId}-${label}`;
      const next = { ...prev };
      delete next[key];
      try { localStorage.setItem("uu-webinar-card-highlights", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const saveEngagement = async (id: string) => {
    const inp = engagementInput[id] ?? {};
    const body: Record<string, number> = {};
    if (inp.addToCalendar !== undefined && inp.addToCalendar !== "") body.addToCalendar = Number(inp.addToCalendar);
    if (inp.joinedWhatsApp !== undefined && inp.joinedWhatsApp !== "") body.joinedWhatsApp = Number(inp.joinedWhatsApp);
    if (Object.keys(body).length === 0) return;
    setSavingEngagement(id);
    try {
      await fetch(`/api/webinar2/engagement-metrics/${id}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      // Invalidate enhanced cache for this webinar
      setEnhancedData((prev) => {
        const next = { ...prev };
        Object.keys(next).filter((k) => k.startsWith(id + "-")).forEach((k) => delete next[k]);
        return next;
      });
    } finally { setSavingEngagement(null); }
  };

  const toggleLowTicketMetric = (label: string) => {
    setHiddenLowTicketMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Load webinar list on mount
  useEffect(() => {
    fetch("/api/webinar2/list")
      .then((r) => r.json())
      .then((d: { success: boolean; webinars: WebinarDef[] }) => {
        if (d.success) {
          const sorted = [...d.webinars].reverse();
          setWebinars(sorted);
          if (sorted.length) setSelectedIds([sorted[0].id]);
        }
      })
      .catch(console.error);
  }, []);

  const loadWebinar = useCallback(async (id: string) => {
    setLoadingIds((prev) => new Set([...prev, id]));
    try {
      const [fRes, rRes, rbsRes, cbsRes] = await Promise.all([
        fetch(`/api/webinar2/funnel/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/retention/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/retention-by-source/${id}`).then((r) => r.json()),
        fetch(`/api/webinar2/cash-by-source/${id}`).then((r) => r.json()),
      ]);
      if (fRes.success) {
        setFunnelCache((prev) => ({ ...prev, [id]: fRes.funnel }));
        if (fRes.funnel.adSpend > 0) {
          setAdSpendInput((prev) => ({ ...prev, [id]: String(fRes.funnel.adSpend) }));
        }
      }
      if (rRes.success) setRetentionCache((prev) => ({ ...prev, [id]: rRes.retention }));
      if (rbsRes.success && rbsRes.retention?.length > 0) {
        setRetentionBySourceCache((prev) => ({ ...prev, [id]: rbsRes.retention }));
      }
      if (cbsRes.success && cbsRes.data?.length > 0) {
        setCashBySourceCache((prev) => ({ ...prev, [id]: cbsRes.data }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, []);

  // Load funnels for newly selected webinars
  useEffect(() => {
    for (const id of selectedIds) {
      if (!funnelCache[id] && !loadingIds.has(id)) loadWebinar(id);
    }
  }, [selectedIds, funnelCache, loadingIds, loadWebinar]);

  // Load enhanced metrics when webinar or filters change
  const loadEnhanced = useCallback(async (id: string, traffic: string, appType: string) => {
    const cacheKey = `${id}-${traffic}-${appType}`;
    setEnhancedLoading((prev) => new Set([...prev, id]));
    try {
      const res = await fetch(`/api/webinar2/enhanced/${id}?traffic=${traffic}&appType=${appType}`);
      const d = await res.json() as { success: boolean; data: EnhancedWebinarData };
      if (d.success) {
        setEnhancedData((prev) => ({ ...prev, [cacheKey]: d.data }));
        if (d.data.onWebinar.pitchMinute) {
          setPitchMinuteInput((prev) => ({ ...prev, [id]: String(d.data.onWebinar.pitchMinute) }));
        }
      }
    } catch (e) {
      console.error("Enhanced fetch error:", e);
    } finally {
      setEnhancedLoading((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, []);

  const primaryId = selectedIds[0] ?? null;

  // Fetch reg timing + optins-by-utm for visible webinars
  useEffect(() => {
    for (const id of selectedIds) {
      if (!regTimingCache[id]) {
        fetch(`/api/webinar2/reg-timing/${id}`).then((r) => r.json()).then((d: { success: boolean; data: { dayOffset: number; label: string; optins: number; attended: number; showRate: number }[] }) => {
          if (d.success) setRegTimingCache((prev) => ({ ...prev, [id]: d.data }));
        }).catch(() => {});
      }
      if (!optinsByUtmCache[id]) {
        fetch(`/api/webinar2/optins-by-utm/${id}`).then((r) => r.json()).then((d: { success: boolean; byCampaign: { name: string; count: number }[]; bySource: { name: string; count: number }[]; total: number }) => {
          if (d.success) setOptinsByUtmCache((prev) => ({ ...prev, [id]: d }));
        }).catch(() => {});
      }
    }
  }, [selectedIds, regTimingCache, optinsByUtmCache]);

  const enhancedFetchRef = useRef<string>("");
  useEffect(() => {
    // Load enhanced data for ALL selected webinars (not just primary)
    for (const id of selectedIds) {
      const traffic = selectedIds.length > 1 ? (cardTraffic[id] ?? trafficFilter) : trafficFilter;
      const key = `${id}-${traffic}-${appTypeFilter}`;
      if (!enhancedData[key] && !enhancedLoading.has(id)) {
        loadEnhanced(id, traffic, appTypeFilter);
      }
    }
    // Track primary for ref-based dedup on filter changes
    const key = `${primaryId}-${trafficFilter}-${appTypeFilter}`;
    if (primaryId && key !== enhancedFetchRef.current) {
      enhancedFetchRef.current = key;
    }
  }, [selectedIds, primaryId, trafficFilter, appTypeFilter, cardTraffic, loadEnhanced, enhancedData, enhancedLoading]);
  // Blended (All) view: the Paid block too, so Cost / Application and Cost / Close divide ad spend by the PAID counts (lib/webinar-metrics.ts).
  useEffect(() => {
    if (trafficFilter !== "all" || !primaryId || selectedIds.length > 1) return;
    if (enhancedData[`${primaryId}-all-${appTypeFilter}`] && !enhancedData[`${primaryId}-paid-${appTypeFilter}`]) loadEnhanced(primaryId, "paid", appTypeFilter);
  }, [trafficFilter, primaryId, selectedIds.length, appTypeFilter, enhancedData, loadEnhanced]);

  // Funnel Compare mode reads the Organic and Paid blocks (latest-opt-in rule): load both for the primary webinar.
  useEffect(() => {
    if (funnelMode !== "compare" || !primaryId || selectedIds.length > 1) return;
    for (const t of ["organic", "paid"] as const) {
      if (!enhancedData[`${primaryId}-${t}-${appTypeFilter}`]) loadEnhanced(primaryId, t, appTypeFilter);
    }
  }, [funnelMode, primaryId, selectedIds.length, appTypeFilter, enhancedData, loadEnhanced]);

  // Sheets (List) view = Cr8Her's "Webinar Velocity Metrics" table 1:1 (Tab for Joey Forslund, Oct 29 2026 4:00 PM ET:
  // "It needs to be the exact same way as the Cr8Her UI"): the selected webinar plus its three predecessors, each column
  // on that webinar's unsegmented all-traffic cohort (Cr8Her pages/Webinars.tsx, sheetComparisonIds).
  const [sheetInfoMetric, setSheetInfoMetric] = useState<string | null>(null);
  const sheetComparisonIds = useMemo(() => {
    const index = webinars.findIndex((webinar) => webinar.id === primaryId);
    return index < 0 ? [] : webinars.slice(index, index + 4).map((webinar) => webinar.id);
  }, [webinars, primaryId]);
  useEffect(() => {
    if (viewMode !== "list") return;
    for (const id of sheetComparisonIds) {
      if (!funnelCache[id] && !loadingIds.has(id)) loadWebinar(id);
      const key = `${id}-all-all`;
      if (!enhancedData[key] && !enhancedLoading.has(id)) loadEnhanced(id, "all", "all");
    }
  }, [viewMode, sheetComparisonIds, funnelCache, loadingIds, enhancedData, enhancedLoading, loadWebinar, loadEnhanced]);
  // Organic / Paid numbers for the list view's one sheet (Tab, Oct 29 2026 7:30 PM ET, Joey: "whenever I click on either All
  // or Organic or Paid, it will adjust the metrics … it's just one sheet"): the same webinars' enhanced blocks on Paid / Organic
  // people only (latest-opt-in rule, /api/velocity-metrics?shape=webinar-traffic), loaded with the view and held, so the
  // "Split by traffic" toggle swaps the sheet at once.
  const [sheetTraffic, setSheetTraffic] = useState<Record<string, EnhancedWebinarData | null>>({});
  const sheetTrafficIds = sheetComparisonIds.join(",");
  useEffect(() => {
    if (viewMode !== "list" || !sheetTrafficIds) return;
    const abort = new AbortController();
    for (const traffic of ["organic", "paid"] as const) {
      const load = (fresh: boolean): Promise<void> => fetch(`/api/velocity-metrics?shape=webinar-traffic&traffic=${traffic}&ids=${encodeURIComponent(sheetTrafficIds)}${fresh ? "&fresh=1" : ""}`, { signal: abort.signal })
        .then((r) => r.json()).then((d) => {
          if (!d?.success) return;
          setSheetTraffic((prev) => { const next = { ...prev }; for (const [id, value] of Object.entries(d.data || {})) next[`${id}-${traffic}`] = (value as EnhancedWebinarData | null) ?? null; return next; });
          if (d.stale && !fresh) return load(true);
        }).catch(() => {});
      load(false);
    }
    return () => abort.abort();
  }, [viewMode, sheetTrafficIds]);


  const savePitchMinute = async (webinarId: string) => {
    const val = parseInt(pitchMinuteInput[webinarId] ?? "");
    if (isNaN(val) || val < 0) return;
    setSavingPitchMinute(webinarId);
    try {
      await fetch(`/api/webinar2/pitch-minute/${webinarId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pitchMinute: val }),
      });
      loadEnhanced(webinarId, trafficFilter, appTypeFilter);
    } catch (e) {
      console.error(e);
    } finally {
      setSavingPitchMinute(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleStageClick = (webinarId: string, label: string, traffic: "all" | "paid" | "organic" = "all") => {
    const stageKey = STAGE_KEY_MAP[label];
    if (!stageKey) return;
    setStageLeadsModal({ webinarId, label, stageKey, traffic });
  };

  const handleUploadSuccess = (webinarId: string) => {
    setUploadModal(null);
    setFunnelCache((prev) => { const n = { ...prev }; delete n[webinarId]; return n; });
    setRetentionCache((prev) => { const n = { ...prev }; delete n[webinarId]; return n; });
    setCashBySourceCache((prev) => { const n = { ...prev }; delete n[webinarId]; return n; });
    loadWebinar(webinarId);
  };

  const handleAddWebinarSuccess = (newId: string) => {
    setAddWebinarModal(false);
    fetch("/api/webinar2/list")
      .then((r) => r.json())
      .then((d: { success: boolean; webinars: WebinarDef[] }) => {
        if (d.success) {
          const sorted = [...d.webinars].reverse();
          setWebinars(sorted);
          setSelectedIds([newId]);
        }
      })
      .catch(console.error);
  };

  const saveAdSpend = async (webinarId: string) => {
    const val = parseFloat(adSpendInput[webinarId] ?? "");
    if (isNaN(val) || val < 0) return;
    setSavingAdSpend(webinarId);
    try {
      await fetch(`/api/webinar2/ad-spend/${webinarId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ adSpend: val }),
      });
      setFunnelCache((prev) => {
        const f = prev[webinarId];
        if (!f) return prev;
        const roas = val > 0 ? f.paidCash / val : 0;
        return { ...prev, [webinarId]: { ...f, adSpend: val, roas } };
      });
    } catch (e) {
      console.error(e);
    } finally {
      setSavingAdSpend(null);
    }
  };

  // ── Derived state ──────────────────────────────────────────────────────────
  const isSingleView = selectedIds.length <= 1;
  const primaryFunnel = primaryId ? funnelCache[primaryId] ?? null : null;
  const primaryRetention = primaryId ? retentionCache[primaryId] ?? [] : [];
  const primaryRetentionBySource = primaryId ? retentionBySourceCache[primaryId] ?? [] : [];
  const primaryCashBySource = primaryId ? cashBySourceCache[primaryId] ?? [] : [];
  const primaryLoading = primaryId ? loadingIds.has(primaryId) : false;

  const funnelStages: FunnelStage[] = primaryFunnel
    ? [
        { label: "Opt-ins", value: primaryFunnel.totalOptins },
        ...(primaryFunnel.hasZoomData
          ? [
              { label: "Attended", value: primaryFunnel.totalAttended, subLabel: `${Math.round(primaryFunnel.avgDuration)}m avg` },
            ]
          : []),
        { label: "Applications", value: primaryFunnel.totalApps },
        { label: "Calls Booked", value: primaryFunnel.totalCalls },
        { label: "Calls Taken", value: primaryFunnel.totalShowed, subLabel: `${Math.round(primaryFunnel.callShowRate)}% show rate` },
        { label: "Closed", value: primaryFunnel.totalWins, subLabel: `${primaryFunnel.callToCloseRate.toFixed(1)}% close rate` },
        { label: "Cash", value: primaryFunnel.totalCash, isCash: true },
      ]
    : [];

  const organicCloseRate = primaryFunnel && primaryFunnel.organicShowed > 0
    ? (primaryFunnel.organicWins / primaryFunnel.organicShowed) * 100 : 0;
  const paidCloseRate = primaryFunnel && primaryFunnel.paidShowed > 0
    ? (primaryFunnel.paidWins / primaryFunnel.paidShowed) * 100 : 0;
  const organicShowRate = primaryFunnel && primaryFunnel.organicCalls > 0
    ? (primaryFunnel.organicShowed / primaryFunnel.organicCalls) * 100 : 0;
  const paidShowRate = primaryFunnel && primaryFunnel.paidCalls > 0
    ? (primaryFunnel.paidShowed / primaryFunnel.paidCalls) * 100 : 0;

  const organicStages: FunnelStage[] = primaryFunnel
    ? [
        { label: "Opt-ins", value: primaryFunnel.organicOptins },
        { label: "Applications", value: primaryFunnel.organicApps },
        { label: "Calls Booked", value: primaryFunnel.organicCalls },
        { label: "Calls Taken", value: primaryFunnel.organicShowed, subLabel: `${Math.round(organicShowRate)}% show rate` },
        { label: "Closed", value: primaryFunnel.organicWins, subLabel: `${organicCloseRate.toFixed(1)}% close rate` },
        { label: "Cash", value: primaryFunnel.organicCash, isCash: true },
      ]
    : [];

  const paidStages: FunnelStage[] = primaryFunnel
    ? [
        { label: "Opt-ins", value: primaryFunnel.paidOptins },
        { label: "Applications", value: primaryFunnel.paidApps },
        { label: "Calls Booked", value: primaryFunnel.paidCalls },
        { label: "Calls Taken", value: primaryFunnel.paidShowed, subLabel: `${Math.round(paidShowRate)}% show rate` },
        { label: "Closed", value: primaryFunnel.paidWins, subLabel: `${paidCloseRate.toFixed(1)}% close rate` },
        { label: "Cash", value: primaryFunnel.paidCash, isCash: true },
      ]
    : [];

  // ── Helpers for multi-card funnels ─────────────────────────────────────────
  const getCardStages = (f: WebinarFunnel, traffic: "all" | "paid" | "organic"): FunnelStage[] => {
    if (traffic === "organic") {
      const sr = f.organicCalls > 0 ? (f.organicShowed / f.organicCalls) * 100 : 0;
      const cr = f.organicShowed > 0 ? (f.organicWins / f.organicShowed) * 100 : 0;
      return [
        { label: "Opt-ins", value: f.organicOptins },
        { label: "Applications", value: f.organicApps },
        { label: "Calls Booked", value: f.organicCalls },
        { label: "Calls Taken", value: f.organicShowed, subLabel: `${Math.round(sr)}% show rate` },
        { label: "Closed", value: f.organicWins, subLabel: `${cr.toFixed(1)}% close rate` },
        { label: "Cash", value: f.organicCash, isCash: true },
      ];
    }
    if (traffic === "paid") {
      const sr = f.paidCalls > 0 ? (f.paidShowed / f.paidCalls) * 100 : 0;
      const cr = f.paidShowed > 0 ? (f.paidWins / f.paidShowed) * 100 : 0;
      return [
        { label: "Opt-ins", value: f.paidOptins },
        { label: "Applications", value: f.paidApps },
        { label: "Calls Booked", value: f.paidCalls },
        { label: "Calls Taken", value: f.paidShowed, subLabel: `${Math.round(sr)}% show rate` },
        { label: "Closed", value: f.paidWins, subLabel: `${cr.toFixed(1)}% close rate` },
        { label: "Cash", value: f.paidCash, isCash: true },
      ];
    }
    const sr = f.totalCalls > 0 ? (f.totalShowed / f.totalCalls) * 100 : 0;
    const cr = f.totalShowed > 0 ? (f.totalWins / f.totalShowed) * 100 : 0;
    return [
      { label: "Opt-ins", value: f.totalOptins },
      { label: "Applications", value: f.totalApps },
      { label: "Calls Booked", value: f.totalCalls },
      { label: "Calls Taken", value: f.totalShowed, subLabel: `${Math.round(sr)}% show rate` },
      { label: "Closed", value: f.totalWins, subLabel: `${cr.toFixed(1)}% close rate` },
      { label: "Cash", value: f.totalCash, isCash: true },
    ];
  };

  // Enhanced data for current selection
  const enhancedKey = primaryId ? `${primaryId}-${trafficFilter}-${appTypeFilter}` : "";
  const enhanced = enhancedKey ? enhancedData[enhancedKey] ?? null : null;
  const enhancedIsLoading = primaryId ? enhancedLoading.has(primaryId) : false;

  // ── Render ─────────────────────────────────────────────────────────────────

  // Helper to render a webinar's content (used in both single and compare views)
  const renderWebinarContent = (id: string, isCompare = false, showHighlights = true) => {
    const f = funnelCache[id];
    const isLoading = loadingIds.has(id);
    const def = webinars.find((w) => w.id === id);
    const traffic = isCompare ? (cardTraffic[id] ?? "all") : trafficFilter;
    const eKey = `${id}-${traffic}-${appTypeFilter}`;
    const enh = enhancedData[eKey] ?? null;
    const enhLoading = enhancedLoading.has(id);
    const ret = retentionCache[id] ?? [];
    const retBySrc = retentionBySourceCache[id] ?? [];
    const cashBySrc = cashBySourceCache[id] ?? [];
    const retSources = retBySrc.length > 0 ? Object.keys(retBySrc[0]).filter((k) => k !== "minute") : [];
    const aud = def?.audienceType;

    if (isLoading) return <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">Loading…</div>;
    if (!f) return <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">No data</div>;

    // Build stage data for funnel
    const stagesOld = isCompare ? getCardStages(f, traffic) : (traffic === "all" ? funnelStages : traffic === "organic" ? organicStages : paidStages);
    // Organic / Paid funnel = the same latest-opt-in blocks as the cards, so chart and cards agree.
    const tb = traffic !== "all" ? enh?.salesTotal ?? null : null;
    // Same counts and rates as the cards and the Projections tab (lib/webinar-metrics.ts): call show = taken ÷ End of Call Reports,
    // close rate = closed ÷ (taken − deposits), show = taken ÷ (taken + no-shows) (Joey, Nov 2 2026: deposits neutral for close, count as shown; cancellations neutral). (Was taken ÷ booked here, so the funnel read "11% show rate" beside the card's 100%.)
    const stagesFromBlock = (b: SalesMetricsBlock, attended?: { value: number; avg: number } | null): FunnelStage[] => [
      { label: "Opt-ins", value: b.optins },
      ...(attended ? [{ label: "Attended", value: attended.value, subLabel: `${Math.round(attended.avg)}m avg` }] : []),
      { label: "Applications", value: b.apps },
      { label: "Calls Booked", value: b.bookedCalls },
      { label: "Calls Taken", value: b.takenCalls, subLabel: `${fmtWebinarMetric("callShowRate", b.takenCalls + (b.noShows || 0) > 0 ? (b.takenCalls / (b.takenCalls + (b.noShows || 0))) * 100 : null)} call show rate (${fmtN(b.takenCalls)} taken, ${fmtN(b.noShows || 0)} no-show${(b.noShows || 0) === 1 ? "" : "s"})` },
      { label: "Closed", value: b.closed, subLabel: `${fmtWebinarMetric("closeRate", b.takenCalls - (b.deposits || 0) > 0 ? (b.closed / (b.takenCalls - (b.deposits || 0))) * 100 : null)} close rate${b.deposits ? ` (${fmtN(b.deposits)} deposit${b.deposits === 1 ? "" : "s"} neutral)` : ""}` },
      { label: "Cash", value: b.cash, isCash: true },
    ];
    // All traffic too: the enhanced block (the cards' numbers), not the older funnel endpoint, so the funnel and the cards agree.
    const allBlock = traffic === "all" && enh ? enh.salesTotal : null;
    const stages: FunnelStage[] = tb ? stagesFromBlock(tb) : allBlock ? stagesFromBlock(allBlock, enh && enh.hasZoomData ? { value: enh.onWebinar.attended, avg: enh.onWebinar.avgDuration } : null) : stagesOld;
    // Funnel "Compare" (Organic | Paid side by side) = the same latest-opt-in blocks as the cards' Organic / Paid (Tab, Oct 29 2026).
    const cmpOrganicBlock = enhancedData[`${id}-organic-${appTypeFilter}`]?.salesTotal ?? null;
    const cmpPaidBlock = enhancedData[`${id}-paid-${appTypeFilter}`]?.salesTotal ?? null;
    const compareOrganicStages = cmpOrganicBlock ? stagesFromBlock(cmpOrganicBlock) : organicStages;
    const comparePaidStages = cmpPaidBlock ? stagesFromBlock(cmpPaidBlock) : paidStages;

    // Pick which sales block to show based on metricsView toggle
    const activeBlock = enh
      ? (metricsView === "on-webby" ? enh.salesOnWebby : metricsView === "post-webby" ? enh.salesPostWebby : enh.salesTotal)
      : null;
    const totalBlock = enh?.salesTotal ?? null;
    const onBlock = enh?.salesOnWebby ?? null;
    const postBlock = enh?.salesPostWebby ?? null;
    // Webinar opt-in conversion uses GA4 visitors for the marketing window, never Zoom registrations.
    const landingPageVisitors = enh?.landingPageVisitors ?? null;

    // Build KPIs from active block (enhanced) or fallback to funnel data
    type KpiCard = { l: string; dl?: string; v: string; num?: number; subCount?: string; onVal?: number; postVal?: number; totalVal?: number; isRate?: boolean; isCash?: boolean; clickable?: boolean; lowerIsBetter?: boolean; tip?: string };
    // Paid (latest-opt-in rule, Joey Oct 29 2026 8:52 PM ET): opt-ins = people whose most recent opt-in came from a Meta ad;
    // landing page visitors = Meta landing page views; Meta's own lead count rides beside as "Meta reports".
    const metaPaid = traffic === "paid" ? enh?.metaPaid ?? null : null;
    const META_PAID_TIP = "Landing page visitors = Meta Ads Manager landing page views";
    const metaOptinsShown = !!metaPaid && metricsView === "total";
    // Card set, order and labels = Cr8Her's Webinars tab, 1:1 (Joey, Oct 29 2026). UU-only cards that Cr8Her doesn't
    // show (Calls Due, Chargeback %) are folded in the way Cr8Her does it (Show Rate reads "taken / due").
    const uuCancelledOf = (block: SalesMetricsBlock | null | undefined) => block ? (block.cancelledBySales + block.cancelledByLead) : 0;
    // Shared metrics for this webinar + traffic + sales view (lib/webinar-metrics.ts), the same figures the Projections tab shows.
    const seg: WebinarSegment = traffic === "paid" ? "paid" : traffic === "organic" ? "organic" : "all";
    const segPaidResp = seg === "all" ? enhancedData[`${id}-paid-${appTypeFilter}`] : null;
    // The Paid response's counts AND ad spend, so Blended ad metrics use the same spend as the Paid view (lib/webinar-metrics.ts).
    const segPaid = segPaidResp?.salesTotal ? { ...segPaidResp.salesTotal, adSpend: segPaidResp.adSpend } : null;
    const sm = enh && activeBlock ? webinarMetrics(enh, seg, { block: activeBlock, paid: segPaid }) : null;
    const uuTakenVal = sm?.taken ?? activeBlock?.takenCalls ?? 0;
    const smT = enh ? webinarMetrics(enh, seg, { paid: segPaid }) : null; // whole-webinar (On-Webinar cards: CPR, show rate)
    // On-Webinar booking / application rates: the On Webby block ONLY, whatever the Total / On Webby / Post Webby toggle says (Joey, Nov 2 2026:
    // "I want the on-webinar metrics tab to only and always be on-webinar metrics, not blended metrics for the entirety of the webinar").
    const smOW = enh ? webinarMetrics(enh, seg, { block: enh.salesOnWebby, paid: segPaid }) : null;
    const salesKpis: KpiCard[] = activeBlock && totalBlock && onBlock && postBlock && sm ? [
      { l: "Opt-ins",         v: fmtN(activeBlock.optins),       num: activeBlock.optins,       totalVal: totalBlock.optins,       onVal: onBlock.optins,       postVal: postBlock.optins, clickable: true,
        ...(metaOptinsShown && metaPaid ? { subCount: `Meta reports: ${fmtN(metaPaid.leads)}`, tip: `Paid opt-ins = people whose most recent opt-in came from a Meta ad link (organic links never count). Meta Ads Manager reports ${fmtN(metaPaid.leads)} leads for the webinar campaigns over the marketing window (Meta also counts people who clicked an ad and signed up another way).` } : {}) },
      ...(landingPageVisitors && landingPageVisitors > 0 ? [{ l: "Landing Page Visitors", v: fmtN(landingPageVisitors), num: landingPageVisitors, isRate: true, ...(metaPaid ? { tip: META_PAID_TIP } : {}) }] : []),
      ...(landingPageVisitors && landingPageVisitors > 0 ? [{ l: "Opt-in Rate", dl: LBL.optinRate, v: fmtWebinarMetric("optinRate", sm.optinRate), num: (activeBlock.optins / landingPageVisitors) * 100, subCount: metaOptinsShown ? `${fmtN(activeBlock.optins)} ÷ ${fmtN(landingPageVisitors)} Meta LP views` : `${fmtN(activeBlock.optins)} / ${fmtN(landingPageVisitors)}`, isRate: true, ...(metaOptinsShown ? { tip: "Opt-in rate = Paid opt-ins (most recent opt-in from a Meta ad, up to 6:30 PM ET on webinar day) ÷ Meta Ads Manager landing page views (webinar campaigns). Callix's Opt-in Rate column in Ads Manager divides by Callix's own tracked site visitors instead (it counts every browser its pixel sees from an ad, about 40% more than Meta's landing page views), so Callix reads lower for the same opt-ins." } : {}) }] : []),
      { l: "Total Apps",      dl: LBL.apps, v: fmtN(activeBlock.apps),         num: activeBlock.apps,         totalVal: totalBlock.apps,         onVal: onBlock.apps,         postVal: postBlock.apps, clickable: true },
      { l: "Full Apps",       v: fmtN(activeBlock.fullApps),     num: activeBlock.fullApps,     totalVal: totalBlock.fullApps,     onVal: onBlock.fullApps,     postVal: postBlock.fullApps, clickable: true },
      { l: "Qualified Apps",  v: fmtN(activeBlock.qualifiedApps), num: activeBlock.qualifiedApps, totalVal: totalBlock.qualifiedApps, onVal: onBlock.qualifiedApps, postVal: postBlock.qualifiedApps, clickable: true, tip: "Qualified = applications whose budget answer (\"Based on your budget, which option best describes you right now?\") is anything except $0-$1,000. Blank = not qualified." },
      { l: "Partial Apps",    v: fmtN(activeBlock.partialApps),  num: activeBlock.partialApps,  totalVal: totalBlock.partialApps,  onVal: onBlock.partialApps,  postVal: postBlock.partialApps, clickable: true },
      { l: "Webinar Bookings", dl: LBL.booked, v: fmtN(activeBlock.bookedCalls), num: activeBlock.bookedCalls, isRate: true, clickable: true,
        ...(metricsView === "total" && (activeBlock.bookedAfterWebinarDay ?? 0) > 0 ? { subCount: `${fmtN(activeBlock.bookedCalls - (activeBlock.bookedAfterWebinarDay ?? 0))} webinar day · ${fmtN(activeBlock.bookedAfterWebinarDay ?? 0)} after` } : {}),
        tip: "Webinar bookings = people in this traffic (Paid / Organic by their most recent opt-in) who booked a call from the webinar day on, whatever day they booked. Callix Ads Manager counts bookings by booking date and the contact's last ad click: a range that ends on the webinar day leaves out the bookings made afterwards, and it also counts bookings from the call-funnel ads (not webinar bookings). Set Callix's range through today to compare." },
      // Booking Rate (Sales Metrics), Joey Nov 3 2026 3:35 PM ET. Follows the Total / On Webby / Post Webby toggle:
      // Total = webinar bookings ÷ opt-ins (capped at the 6:30 PM ET cutoff); On Webby = on-webby bookings ÷ people at pitch (same as the
      // On-Webinar Metrics card); Post Webby = post-webby bookings ÷ people who applied after the webinar. Same on/post split as Webinar Bookings.
      ...(() => {
        const brNum = metricsView === "on-webby" ? onBlock.bookedCalls : metricsView === "post-webby" ? postBlock.bookedCalls : totalBlock.bookedCalls;
        const brDen = metricsView === "on-webby" ? (enh?.onWebinar.attendeesAtPitch || 0) : metricsView === "post-webby" ? postBlock.apps : totalBlock.optins;
        const brRate = brDen > 0 ? (brNum / brDen) * 100 : null;
        const brOf = metricsView === "on-webby" ? "at pitch" : metricsView === "post-webby" ? "applied after webby" : "opt-ins";
        const brTip = metricsView === "on-webby"
          ? "On Webby booking rate = calls booked by on-webinar applicants ÷ people still on at the pitch (Jeremy AI: 30% benchmark, 20% average). Same number as the On-Webinar Metrics Booking Rate."
          : metricsView === "post-webby"
          ? "Post Webby booking rate = calls booked by people who applied after the webinar ended ÷ everyone who applied after the webinar. Jeremy AI would divide post-webby bookings by the setter-worked pool instead (all opt-ins minus on-webby bookings)."
          : "Total booking rate = every webinar booking (on webby + post webby) ÷ all webinar opt-ins (up to 6:30 PM ET on webinar day). Jeremy AI calls this the blended booking rate: it folds in the webinar show rate, so don't hold it to the 30% at-pitch benchmark.";
        return [{ l: "Sales Booking Rate", dl: "Booking Rate", v: brRate === null ? "—" : fmtPct(brRate), num: brRate ?? 0, isRate: true,
          subCount: `${fmtN(brNum)} booked ÷ ${fmtN(brDen)} ${brOf}`, tip: brTip } as KpiCard];
      })(),
      { l: "Missing Outcome", v: "" }, // Calls Missing Outcome (Tab, Nov 3 2026): rendered by MissingOutcomeCard below
      { l: "End of Call Reports Filled", dl: LBL.eocs, v: fmtN(sm.eocs ?? 0), num: sm.eocs ?? 0, isRate: true, clickable: true,
        subCount: `${LBL.eocShare}: ${fmtWebinarMetric("eocShare", sm.eocShare)} (${fmtN(sm.eocs ?? 0)} of ${fmtN(sm.booked ?? 0)})`,
        tip: "Booked calls with an outcome logged in an End of Call Report. \"Bookings with an EOC (so far)\" = reports ÷ webinar bookings: the rest haven't happened yet or have no report, so calls taken, closes and cash are still coming in. The Projections tab's Webinar Actuals uses the same two numbers." },
      { l: "Calls Taken",     dl: LBL.taken, v: fmtN(uuTakenVal),               num: uuTakenVal,               totalVal: totalBlock.takenCalls,   onVal: onBlock.takenCalls,   postVal: postBlock.takenCalls, clickable: true },
      { l: "Show Rate", dl: LBL.callShowRate,
        v: fmtWebinarMetric("callShowRate", sm.callShowRate),
        num: sm.callShowRate ?? 0,
        subCount: webinarMetricHow("callShowRate", sm),
        tip: "Calls with an End of Call Report filed: taken ÷ EOCs. Booked calls with no EOC yet aren't in it (see Bookings with an EOC on the End of Call Reports card). Callix's Show % counts only calls Callix recorded (its notetaker or a logged call), so it reads lower when calls weren't recorded there.",
        isRate: true },
      ...(totalBlock.noShows > 0 ? [{ l: "No Shows", v: fmtN(activeBlock.noShows), num: activeBlock.noShows, totalVal: totalBlock.noShows, onVal: onBlock.noShows, postVal: postBlock.noShows, lowerIsBetter: true, clickable: true }] : []),
      ...(uuCancelledOf(totalBlock) > 0 ? [{
        l: "Cancelled Calls",
        v: `${fmtN(uuCancelledOf(activeBlock))} (${activeBlock.bookedCalls > 0 ? `${Math.round(uuCancelledOf(activeBlock) / activeBlock.bookedCalls * 100)}%` : "—"})`,
        subCount: `of ${fmtN(activeBlock.bookedCalls)} webinar bookings`,
        totalVal: uuCancelledOf(totalBlock), onVal: uuCancelledOf(onBlock), postVal: uuCancelledOf(postBlock), lowerIsBetter: true,
      }] : []),
      { l: "Closes",          dl: LBL.closes, v: fmtN(sm.closes ?? 0), num: sm.closes ?? 0, ...(sm.deposits > 0 ? { subCount: `+ ${fmtN(sm.deposits)} deposit${sm.deposits === 1 ? "" : "s"} (not a close)` } : {}),       totalVal: totalBlock.closed,       onVal: onBlock.closed,       postVal: postBlock.closed, clickable: true },
      ...(activeBlock.deposits > 0 ? [{ l: "Deposits", v: fmtN(activeBlock.deposits), num: activeBlock.deposits, totalVal: totalBlock.deposits, onVal: onBlock.deposits, postVal: postBlock.deposits, clickable: true }] : []),
      { l: "Close Rate",      dl: LBL.closeRate, v: fmtWebinarMetric("closeRate", sm.closeRate), num: activeBlock.closeRate, subCount: webinarMetricHow("closeRate", sm), isRate: true },
      { l: "Cash",            dl: LBL.cash, v: fmtWebinarMetric("cash", sm.cash),         num: activeBlock.cash,         totalVal: totalBlock.cash,         onVal: onBlock.cash,         postVal: postBlock.cash, isCash: true, clickable: true, tip: "Cash from this traffic's End of Call Reports after the webinar, whatever day it was paid. Callix counts a payment on its payment date, so match its range through the payment day." },
      { l: "AOV",             dl: LBL.cashPerClose, v: fmtWebinarMetric("cashPerClose", sm.cashPerClose), num: sm.cashPerClose ?? undefined, ...(sm.cashPerClose !== null ? { subCount: webinarMetricHow("cashPerClose", sm) } : {}), isRate: true },
      { l: "Cash/Call",       v: activeBlock.cashPerCall > 0 ? fmt$(activeBlock.cashPerCall) : "—", num: activeBlock.cashPerCall > 0 ? activeBlock.cashPerCall : undefined, isRate: true },
      ...(metricsView === "total" ? [{ l: "Cost/Booking", dl: LBL.costPerBooking, v: fmtWebinarMetric("costPerBooking", sm.costPerBooking), num: sm.costPerBooking ?? undefined, subCount: webinarMetricHow("costPerBooking", sm), isRate: true, lowerIsBetter: true, tip: "Ad spend ÷ PAID webinar bookings, on Paid and All (Joey, Oct 29 2026 11:07 PM ET: based on what we spent on ads and the calls from ads). Organic has no ad spend. Callix's Cost/Call divides spend by every booking credited to an ad in its date range (incl. call-funnel bookings)." }] : []),
      { l: "Full App→Booking", dl: LBL.appToBooking, v: fmtWebinarMetric("appToBooking", sm.appToBooking), num: activeBlock.appToBookingRate, subCount: webinarMetricHow("appToBooking", sm), isRate: true },
      { l: "Optin→Booking",   v: fmtPct(activeBlock.optins > 0 ? (activeBlock.bookedCalls / activeBlock.optins) * 100 : 0), num: activeBlock.optins > 0 ? (activeBlock.bookedCalls / activeBlock.optins) * 100 : 0, subCount: `${fmtN(activeBlock.optins)} / ${fmtN(activeBlock.bookedCalls)}`, isRate: true },
      { l: "Opt-in→Full App", v: fmtPct(activeBlock.optins > 0 ? (activeBlock.fullApps / activeBlock.optins) * 100 : 0), num: activeBlock.optins > 0 ? (activeBlock.fullApps / activeBlock.optins) * 100 : 0, subCount: `${fmtN(activeBlock.optins)} / ${fmtN(activeBlock.fullApps)}`, isRate: true },
      { l: "Optin→Qual App",  v: fmtPct(activeBlock.optins > 0 ? (activeBlock.qualifiedApps / activeBlock.optins) * 100 : 0), num: activeBlock.optins > 0 ? (activeBlock.qualifiedApps / activeBlock.optins) * 100 : 0, subCount: `${fmtN(activeBlock.optins)} / ${fmtN(activeBlock.qualifiedApps)}`, isRate: true },
      { l: "Qual→Booking",    v: activeBlock.qualifiedApps > 0 ? fmtPct(activeBlock.qualifiedToBookingRate) : "—", num: activeBlock.qualifiedApps > 0 ? activeBlock.qualifiedToBookingRate : undefined, subCount: `${fmtN(activeBlock.qualifiedBooked)} / ${fmtN(activeBlock.qualifiedApps)} qual`, isRate: true },
      { l: "Add to Calendar", v: enh.addToCalendar > 0 ? fmtPct((enh.addToCalendar / Math.max(activeBlock.optins, 1)) * 100) : "—", num: enh.addToCalendar > 0 ? (enh.addToCalendar / Math.max(activeBlock.optins, 1)) * 100 : undefined, subCount: `${fmtN(enh.addToCalendar)} / ${fmtN(activeBlock.optins)}`, isRate: true },
      { l: "Joined WhatsApp", v: enh.joinedWhatsApp > 0 ? fmtPct((enh.joinedWhatsApp / Math.max(activeBlock.optins, 1)) * 100) : "—", num: enh.joinedWhatsApp > 0 ? (enh.joinedWhatsApp / Math.max(activeBlock.optins, 1)) * 100 : undefined, subCount: `${fmtN(enh.joinedWhatsApp)} / ${fmtN(activeBlock.optins)}`, isRate: true },
      ...(enh.adSpend > 0 ? [
        { l: "Ad Spend", dl: LBL.spend, v: fmtWebinarMetric("spend", smT?.spend ?? enh.adSpend), num: smT?.spend ?? enh.adSpend, isRate: true },
        ...(metricsView === "total" && seg !== "organic" ? [
          { l: "Cost/Application", dl: LBL.costPerApp, v: fmtWebinarMetric("costPerApp", sm.costPerApp), num: sm.costPerApp ?? undefined, subCount: webinarMetricHow("costPerApp", sm), isRate: true, lowerIsBetter: true, tip: "Ad spend ÷ PAID applications (All divides by the Paid view's applications)." },
          { l: "Cost/Close", dl: LBL.costPerClose, v: fmtWebinarMetric("costPerClose", sm.costPerClose), num: sm.costPerClose ?? undefined, subCount: webinarMetricHow("costPerClose", sm), isRate: true, lowerIsBetter: true, tip: "Ad spend ÷ PAID closes (deposits are not closes; All divides by the Paid view's closes)." },
        ] : []),
        { l: "Upfront ROAS", v: activeBlock.upfrontRoas > 0 ? `${activeBlock.upfrontRoas.toFixed(2)}x` : "—", num: activeBlock.upfrontRoas, subCount: formatWebinarRoasCashAmounts(activeBlock.upfrontRoas * enh.adSpend, enh.adSpend), isRate: true },
        { l: "ROAS", dl: LBL.roas, v: fmtWebinarMetric("roas", sm.roas), num: sm.roas ?? 0, subCount: webinarMetricHow("roas", sm), isRate: true },
      ] : []),
    ] : [
      { l: "Opt-ins",       v: fmtN(traffic === "paid" ? f.paidOptins : traffic === "organic" ? f.organicOptins : f.totalOptins), clickable: true },
      { l: "Full Apps",     v: fmtN(traffic === "paid" ? f.paidApps : traffic === "organic" ? f.organicApps : f.totalApps), clickable: true },
      { l: "Calls Booked",  v: fmtN(traffic === "paid" ? f.paidCalls : traffic === "organic" ? f.organicCalls : f.totalCalls), clickable: true },
      { l: "Calls Taken",   v: fmtN(traffic === "paid" ? f.paidShowed : traffic === "organic" ? f.organicShowed : f.totalShowed), clickable: true },
      { l: "Show Rate",     v: fmtPct(f.callShowRate), isRate: true },
      { l: "Closes",        v: fmtN(traffic === "paid" ? f.paidWins : traffic === "organic" ? f.organicWins : f.totalWins), clickable: true },
      { l: "Close Rate",    v: fmtPct(f.callToCloseRate), isRate: true },
      { l: "Cash",          v: fmt$(traffic === "paid" ? f.paidCash : traffic === "organic" ? f.organicCash : f.totalCash), clickable: true },
      ...(f.adSpend > 0 ? [
        { l: "Ad Spend", v: fmt$(f.adSpend), isRate: true },
        { l: "Upfront ROAS", v: f.upfrontRoas > 0 ? `${f.upfrontRoas.toFixed(1)}x` : "—", isRate: true },
        { l: "ROAS", v: f.roas > 0 ? `${f.roas.toFixed(1)}x` : "—", isRate: true },
      ] : []),
    ];

    // ── List view early return ──────────────────────────────────────────────
    if (viewMode === "list" && !isCompare) {
      const comparisonColumns = sheetComparisonIds.map((webinarId) => ({ id: webinarId, def: webinars.find((webinar) => webinar.id === webinarId), funnel: funnelCache[webinarId], enhanced: enhancedData[`${webinarId}-all-all`] }));
      if (comparisonColumns.some((column) => !column.funnel || !column.enhanced)) return <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">Loading selected webinar and up to three preceding webinars…</div>;
      const fmtSheet = (value: number | null, kind: "count" | "money" | "rate" = "count") => value === null || value === undefined || !Number.isFinite(value) ? "—" : kind === "money" ? fmt$(value) : kind === "rate" ? fmtPct(value) : fmtN(value);
      const sheetValue = (metric: string, column: { funnel?: WebinarFunnel; enhanced?: EnhancedWebinarData }): [number | null, "count" | "money" | "rate"] => {
        if (!column.enhanced) return [null, "count"];
        const se = column.enhanced; const sales = se.salesTotal; const booked = sales.bookedCalls; const closed = sales.closed; const shown = se.totalEocsTaken; // deposits neutral for close (Joey, Nov 2 2026) const due = se.totalEocsDue;
        const rateOf = (top: number, bottom: number) => bottom > 0 ? top / bottom * 100 : null;
        const values: Record<string, [number | null, "count" | "money" | "rate"]> = {
          "Opt-ins": [sales.optins, "count"], "Landing Page Visitors": [se.landingPageVisitors || null, "count"], "Opt-in Rate": [se.landingPageVisitors ? rateOf(sales.optins, se.landingPageVisitors) : null, "rate"], "Applications": [sales.apps, "count"], "Leads": [sales.apps, "count"], "Full Applications": [sales.fullApps, "count"], "Partial Applications": [sales.partialApps, "count"], "Qualified Applications": [sales.qualifiedApps, "count"], "Booked Calls": [booked, "count"], "Total Booked Closing Calls (All)": [booked, "count"], "EOCs": [due, "count"], "Showed Calls": [shown, "count"], "Total Closing Calls Completed": [shown, "count"], "Closed Deals": [closed, "count"], "Deals Closed": [closed, "count"], "Cash Collected": [sales.cash, "money"],
          "Lead to Booking % (ALL)": [rateOf(booked, sales.apps), "rate"], "Closing Show %": [rateOf(shown, due), "rate"], "Closing %": [rateOf(closed, Math.max(0, shown - (sales.deposits || 0))), "rate"], "Lead to Close %": [rateOf(closed, sales.apps), "rate"], "Closing Call Booked To Close %": [rateOf(closed, booked), "rate"], "Average Cash per Application": [sales.apps ? sales.cash / sales.apps : null, "money"], "Average Cash per Booked Call": [booked ? sales.cash / booked : null, "money"], "Avg Cash / Call": [shown ? sales.cash / shown : null, "money"],
          "On-Webinar Attended": [se.onWebinar.attended, "count"], "On-Webinar Show Rate": [se.onWebinar.showRate, "rate"], "At Pitch": [se.onWebinar.attendeesAtPitch || null, "count"], "On-Webinar Booking Rate": [se.onWebinar.attendeesAtPitch ? rateOf(se.salesOnWebby.bookedCalls, se.onWebinar.attendeesAtPitch) : null, "rate"], "Total App Rate at Pitch": [se.onWebinar.attendeesAtPitch ? rateOf(se.salesOnWebby.apps, se.onWebinar.attendeesAtPitch) : null, "rate"], "Full App Rate at Pitch": [se.onWebinar.attendeesAtPitch ? rateOf(se.salesOnWebby.fullApps, se.onWebinar.attendeesAtPitch) : null, "rate"], "Add to Calendar": [se.addToCalendar || null, "count"], "Joined WhatsApp": [se.joinedWhatsApp || null, "count"],
        };
        return values[metric] ?? [null, "count"];
      };
      const comparisonGroups: Array<[string, string[]]> = [
        ["Webinar funnel", ["Opt-ins", "Landing Page Visitors", "Opt-in Rate", "Applications", "Full Applications", "Partial Applications", "Qualified Applications", "Booked Calls", "EOCs", "Showed Calls", "Closed Deals", "Cash Collected"]],
        ["Sales activity", ["Leads", "Bad Data / Junk", "Outbound Calls", "Connected", "Total Booked Closing Calls (IB)", "Total Booked Closing Calls (OB)", "Total Booked Closing Calls (All)", "Total Closing Calls Completed", "Offers Made", "Deals Closed"]],
        ["Velocity metrics", ["Bad Data %", "Connect To Set Avg", "Avg OB Touch Density", "Connect Rate %", "Lead to Booking % (OB)", "Lead to Booking % (IB)", "Lead to Booking % (ALL)", "Closing Show %", "Offer %", "Closing %"]],
        ["Performance metrics", ["Lead to Close %", "Closing Call Booked To Close %", "Offer To Close %", "Average Cash per Application", "Average Cash per Booked Call", "Avg Cash / Call"]],
        ["Webinar-specific", ["On-Webinar Attended", "On-Webinar Show Rate", "At Pitch", "On-Webinar Booking Rate", "Total App Rate at Pitch", "Full App Rate at Pitch", "Add to Calendar", "Joined WhatsApp"]],
      ];
      // Look = Book a call → Sheets / Velocity Metrics (shared components/SheetTable), targets = UU's own Jeremy Haynes AI
      // $200K-model webinar rows (config/velocity-targets.ts, one webinar scored against one week's webinar target, as the
      // Webinars cards do). Rows with no UU target show the value only (Tab for Joey Forslund, Oct 29 2026 4:51 PM ET).
      const wvSheetPlan = computePlan("jeremy");
      const wvWebinarTargetRows = VELOCITY_TARGET_ROWS.webinar;
      const wvSHEET_TARGET_ROW: Record<string, [string, string]> = {
        "Opt-ins": ["Sales Metrics", "Opt-ins"], "Landing Page Visitors": ["Sales Metrics", "Landing Page Visitors"], "Opt-in Rate": ["Sales Metrics", "Opt-in Rate"],
        "Applications": ["Sales Metrics", "Total Apps"], "Full Applications": ["Sales Metrics", "Full Apps"], "Partial Applications": ["Sales Metrics", "Partial Apps"], "Qualified Applications": ["Sales Metrics", "Qualified Apps"],
        "Booked Calls": ["Sales Metrics", "Webinar Bookings"], "EOCs": ["Sales Metrics", "EOCs Filled"], "Showed Calls": ["Sales Metrics", "Calls Taken"], "Closed Deals": ["Sales Metrics", "Closes"], "Cash Collected": ["Sales Metrics", "Cash"],
        "Leads": ["Sales Metrics", "Total Apps"],
        "Total Booked Closing Calls (IB)": ["Sales activity", "Total Booked Closing Calls (IB)"], "Total Booked Closing Calls (OB)": ["Sales activity", "Total Booked Closing Calls (OB)"], "Total Booked Closing Calls (All)": ["Sales activity", "Total Booked Closing Calls (All)"],
        "Total Closing Calls Completed": ["Sales activity", "Total Closing Calls Completed"], "Offers Made": ["Sales activity", "Offers Made"], "Deals Closed": ["Sales activity", "Deals Closed"],
        "Connect To Set Avg": ["Sales velocity", "Connect To Set Avg"], "Avg OB Touch Density": ["Sales velocity", "Avg OB Touch Density"], "Connect Rate %": ["Sales velocity", "Connect Rate %"],
        "Lead to Booking % (OB)": ["Sales velocity", "Lead to Booking % (OB)"], "Lead to Booking % (IB)": ["Sales velocity", "Lead to Booking % (IB)"], "Lead to Booking % (ALL)": ["Sales velocity", "Lead to Booking % (ALL)"],
        "Closing Show %": ["Sales velocity", "Closing Show %"], "Offer %": ["Sales velocity", "Offer %"], "Closing %": ["Sales velocity", "Closing %"],
        "Lead to Close %": ["Rates", "Lead to Close %"], "Closing Call Booked To Close %": ["Rates", "Closing Call Booked To Close %"],
        "Average Cash per Application": ["Cash efficiency", "Average Cash per Application"], "Average Cash per Booked Call": ["Cash efficiency", "Average Cash per Booked Call"], "Avg Cash / Call": ["Cash efficiency", "Avg Cash / Call"],
        "On-Webinar Attended": ["On-Webinar Metrics", "Attended"], "On-Webinar Show Rate": ["On-Webinar Metrics", "Show Rate"], "On-Webinar Booking Rate": ["On-Webinar Metrics", "Booking Rate"],
        "Total App Rate at Pitch": ["On-Webinar Metrics", "Total App Rate"], "Full App Rate at Pitch": ["On-Webinar Metrics", "Full App Rate"],
      };
      const wvSheetTargetRow = (metric: string) => { const key = wvSHEET_TARGET_ROW[metric]; return key ? wvWebinarTargetRows.find((r) => r.section === key[0] && r.label === key[1]) : undefined; };
      const wvSheetDefinition = (metric: string, row?: VelocityTargetRow) => row ? `${metric}. Urban Unity target per webinar: ${row.derived} (${row.confidence}).` : `${metric}. No Urban Unity webinar target for this metric; value only.`;
      const wvBuildRows = (cols: typeof comparisonColumns): SheetExportRow[] => comparisonGroups.flatMap(([section, metrics]) => metrics.map((metric) => {
        const row = wvSheetTargetRow(metric);
        const metaRow = wvTraffic === "paid" && (metric === "Opt-ins" || metric === "Landing Page Visitors" || metric === "Opt-in Rate") && cols.some((column) => column.enhanced?.metaPaid);
        const definition = metaRow ? `${metric === "Opt-in Rate" ? "Opt-in rate = Paid opt-ins (most recent opt-in from a Meta ad) ÷ Meta landing page views" : metric === "Opt-ins" ? `Opt-ins = people whose most recent opt-in came from a Meta ad link. Meta reports: ${cols.map((column) => column.enhanced?.metaPaid ? fmtN(column.enhanced.metaPaid.leads) : "—").join(" / ")} leads` : "Landing page visitors = Meta Ads Manager landing page views"} of the webinar campaigns over each webinar's marketing window (Book a call "UUCall" campaigns excluded). ${wvSheetDefinition(metric, row)}` : wvSheetDefinition(metric, row);
        const benchmark = uuSheetBenchmark(row, wvSheetPlan, 7, definition);
        const benchmarks = cols.map(() => benchmark);
        const pairs = cols.map((column) => sheetValue(metric, column));
        const rawValues = pairs.map(([value]) => value === null || value === undefined || !Number.isFinite(value) ? null : value);
        return { section, metric, definition, target: benchmark?.target || "N/A", benchmark, benchmarks, targets: benchmarks.map((b) => b?.target ?? ""), rawValues, statusValues: rawValues, values: pairs.map(([value, kind]) => { if (value === null || value === undefined || !Number.isFinite(value)) return "N/A"; return kind === "money" ? `$${Math.round(value).toLocaleString("en-US")}` : fmtSheet(value, kind); }) };
      }));
      // The sheet on show = the "Split by traffic" pick: All = Blended (the webinars' all-traffic blocks), Organic / Paid = sheetTraffic.
      const wvTraffic = trafficFilter;
      const wvTrafficReady = wvTraffic === "all" || comparisonColumns.every((column) => `${column.id}-${wvTraffic}` in sheetTraffic);
      const wvShownColumns = wvTraffic === "all" ? comparisonColumns : comparisonColumns.map((column) => ({ ...column, enhanced: sheetTraffic[`${column.id}-${wvTraffic}`] ?? undefined }));
      const wvSheetRows: SheetExportRow[] = wvBuildRows(wvShownColumns);
      const wvToSections = (list: SheetExportRow[]) => list.reduce<Array<{ title: string; rows: SheetExportRow[] }>>((groups, row) => { const last = groups.at(-1); if (last?.title === row.section) last.rows.push(row); else groups.push({ title: row.section, rows: [row] }); return groups; }, []);
      const wvSheetSections = wvToSections(wvSheetRows);
      const wvTableColumns = comparisonColumns.map((column) => ({ key: column.id, label: formatDate(column.funnel?.webinarDate ?? column.def?.date ?? ""), sub: column.id, title: `${column.id} · ${formatDate(column.funnel?.webinarDate ?? column.def?.date ?? "")}` }));
      const wvSheetHeaders = comparisonColumns.map((column) => `${column.id} ${formatDate(column.funnel?.webinarDate ?? column.def?.date ?? "")}`);
      const wvSheetFile = `webinar-velocity-metrics-${wvTraffic === "all" ? "blended" : wvTraffic}-${comparisonColumns[0]?.funnel?.webinarDate ?? comparisonColumns[0]?.def?.date ?? "export"}`;
      const wvSheetExportCell = (row: SheetExportRow, index: number) => { const value = row.values[index] ?? "N/A"; const b = row.benchmarks[index]; if (!b?.target) return value; const s = sheetTargetStatus(row.statusValues[index] ?? null, b); const m = s === "above" ? "↑ " : s === "below" ? "↓ " : s === "within" ? "✓ " : ""; return `${value} (${m}${b.target})`; };
      const wvSheetMatrix = [[`Urban Unity Webinar Velocity Metrics · ${TRAFFIC_LABEL[wvTraffic]}`], [], ["#", "Metric", ...wvSheetHeaders], ...wvSheetSections.flatMap((section) => [["", section.title, ...wvSheetHeaders.map(() => "")], ...section.rows.map((row) => [String(wvSheetRows.indexOf(row) + 1), row.metric, ...row.values.map((_, i) => wvSheetExportCell(row, i))])])];
      const wvSheetCSV = () => downloadExportBlob(new Blob([wvSheetMatrix.map((cells) => cells.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n")], { type: "text/csv;charset=utf-8" }), `${wvSheetFile}.csv`);
      const wvSheetExcel = () => { const ws = XLSX.utils.aoa_to_sheet(wvSheetMatrix); ws["!cols"] = [{ wch: 6 }, { wch: 34 }, ...wvSheetHeaders.map(() => ({ wch: 28 }))]; const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Webinar Velocity"); XLSX.writeFile(wb, `${wvSheetFile}.xlsx`); };
      const wvSheetPDF = () => downloadExportBlob(buildTablePdf(wvSheetHeaders, wvSheetSections, `${TRAFFIC_LABEL[wvTraffic]} · Selected webinar + 3 preceding · Urban Unity targets (Jeremy Haynes AI $200K/month plan)`, "Webinar Velocity Metrics"), `${wvSheetFile}.pdf`);
      const wvSheetExportButton = "rounded border border-border bg-card px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[var(--brand-primary)]/50 hover:text-foreground";
      const wvInfoSheetRow = sheetInfoMetric ? wvSheetRows.find((r) => r.metric === sheetInfoMetric) : undefined;
      const wvInfoTargetRow = sheetInfoMetric ? wvSheetTargetRow(sheetInfoMetric) : undefined;
      return <div className="space-y-3" data-webinar-velocity-sheet>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h2 className="text-xl font-semibold text-foreground">Webinar Velocity Metrics</h2><p className="text-xs text-muted-foreground">Selected webinar + the 3 before it, each on its own cohort. Targets are Urban Unity's per-webinar plan (Jeremy Haynes AI, $200K/month); rows without one show the value only.</p></div>
          <div className="flex flex-wrap items-center gap-2">{(["CSV", "Excel", "PDF"] as const).map((kind) => <button key={kind} onClick={kind === "CSV" ? wvSheetCSV : kind === "Excel" ? wvSheetExcel : wvSheetPDF} disabled={!wvTrafficReady} title={`Export the ${TRAFFIC_LABEL[wvTraffic]} sheet`} className={wvSheetExportButton}>↓ {kind}</button>)}</div>
        </div>
        <TrafficSectionTitle traffic={wvTraffic} className="flex items-center gap-2" loading={!wvTrafficReady} extra={wvTraffic === "all" ? undefined : (wvTraffic === "paid" ? "Paid = each person's most recent opt-in came from a Meta ad link (organic links never count). Landing page visitors = Meta Ads Manager landing page views of the webinar campaigns over each webinar's marketing window (Book a call \"UUCall\" campaigns excluded); opt-in rate = Paid opt-ins ÷ landing page views. Applications, bookings, shows, closes and cash follow each person's latest opt-in before the application / booking. Zoom attendance (attended, at pitch) is the whole webinar's, not split by traffic." : "Landing page visitors = all site visitors (GA4) minus the Meta landing page views. Ad spend belongs to Paid, so cost and ROAS rows are blank here. Zoom attendance (attended, at pitch) is the whole webinar's, not split by traffic.")} />
        <SheetHighlight resetKey={sheetTrafficIds}>
          {wvTrafficReady ? <SheetTable columns={wvTableColumns} sections={wvSheetSections} onInfo={setSheetInfoMetric} metricWidth={240} columnWidth={165}
            targetTitle={(row, index, label) => `${label}: ${row.targets[index]} per webinar (Urban Unity target)`} /> : <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">Loading {wvTraffic} numbers…</div>}
        </SheetHighlight>
        {sheetInfoMetric && <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label={`${sheetInfoMetric} definition`} onClick={() => setSheetInfoMetric(null)}><div className="w-full max-w-lg rounded-xl border border-border bg-card p-5 shadow-xl" onClick={(event) => event.stopPropagation()}><div className="flex items-start justify-between gap-4"><div><h2 className="text-base font-bold text-foreground">{sheetInfoMetric}</h2><p className="mt-1 text-sm text-muted-foreground">{wvInfoSheetRow?.definition ?? "Definition pending."}</p><p className="mt-3 text-sm font-semibold text-foreground">{wvInfoSheetRow?.benchmark?.target ? <>Target per webinar: <span className="text-[var(--brand-primary)]">{wvInfoSheetRow.benchmark.target}</span> <span className="font-normal text-muted-foreground">(Urban Unity webinar target)</span></> : "No Urban Unity target for this metric."}</p>{wvInfoTargetRow ? <p className="mt-2 text-xs text-muted-foreground">Jeremy's 6-month actual: {wvInfoTargetRow.sixMonth}</p> : null}</div><button onClick={() => setSheetInfoMetric(null)} className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted">Close</button></div></div></div>}
      </div>;
      type SheetRow = { section: string; metric: string; value: string; notes: string; target?: string; indicator?: "above" | "in" | "below" };

      // Compute rangeStr + indicator from UU's TargetRange (resolveTarget returns raw range, not pre-computed display)
      const computeDisplay = (label: string, val: number, _optins: number, isOw = false): { rangeStr: string; indicator: "above" | "in" | "below" } | null => {
        const tgt = resolveTarget(label, aud, isOw);
        return tgt && Number.isFinite(val) ? targetDisplay(tgt, val) : null;
      };

      const makeTrendIcon = (indicator: "above" | "in" | "below", color: string) =>
        indicator === "above" ? (
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="2,11 5.5,7 8.5,9 12,3" /><polyline points="9,3 12,3 12,6" />
          </svg>
        ) : indicator === "below" ? (
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="2,3 5.5,7 8.5,5 12,11" /><polyline points="9,11 12,11 12,8" />
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="7" cy="7" r="5" />
          </svg>
        );

      // Build rows for one sales block (used for display and export)
      const buildBlockRows = (block: typeof totalBlock, sectionLabel: string, takenCalls: number): SheetRow[] => {
        if (!block) return [];
        const rows: SheetRow[] = [];
        const targetBase = enh?.onWebinar.attendeesAtPitch || block.optins;
        const push = (metric: string, value: string, notes: string, val?: number) => {
          const d = val !== undefined ? computeDisplay(metric, val, targetBase) : null;
          rows.push({ section: sectionLabel, metric, value, notes, target: d?.rangeStr, indicator: d?.indicator });
        };
        push("Opt-ins", fmtN(block.optins), "", block.optins);
        if (landingPageVisitors !== null) push("Landing Page Visitors", fmtN(landingPageVisitors), "");
        if (landingPageVisitors && landingPageVisitors > 0) push("Opt-in Rate", fmtPct((block.optins / landingPageVisitors) * 100), `${fmtN(block.optins)} / ${fmtN(landingPageVisitors)}`, (block.optins / landingPageVisitors) * 100);
        push("Total Apps", fmtN(block.apps), "", block.apps);
        push("Full Apps", fmtN(block.fullApps), "", block.fullApps);
        push("Qualified Apps", fmtN(block.qualifiedApps), "", block.qualifiedApps);
        push("Calls Booked", fmtN(block.bookedCalls), "", block.bookedCalls);
        rows.push({ section: sectionLabel, metric: "Calls Taken", value: fmtN(takenCalls), notes: "" });
        rows.push({ section: sectionLabel, metric: "Closes", value: fmtN(block.closed), notes: "" });
        if (block.deposits > 0) rows.push({ section: sectionLabel, metric: "Deposits", value: fmtN(block.deposits), notes: "" });
        push("Close Rate", fmtPct(block.closeRate), "", block.closeRate);
        rows.push({ section: sectionLabel, metric: "Cash", value: fmt$(block.cash), notes: "" });
        if (block.auv > 0) rows.push({ section: sectionLabel, metric: "AOV", value: fmt$(block.auv), notes: "" });
        if (block.showRate > 0) push("Show Rate", fmtPct(block.showRate), "", block.showRate);
        push("Full App→Booking", fmtPct(block.appToBookingRate), `${fmtN(block.bookedCalls)} booked ÷ ${fmtN(block.apps)} apps`, block.appToBookingRate);
        const ob = block.optins > 0 ? (block.bookedCalls / block.optins) * 100 : 0;
        push("Opt-in→Booking", fmtPct(ob), `${fmtN(block.optins)} / ${fmtN(block.bookedCalls)}`, ob);
        const ofa = block.optins > 0 ? (block.fullApps / block.optins) * 100 : 0;
        push("Opt-in→Full App", fmtPct(ofa), `${fmtN(block.optins)} / ${fmtN(block.fullApps)}`, ofa);
        const oqa = block.optins > 0 ? (block.qualifiedApps / block.optins) * 100 : 0;
        push("Opt-in→Qual App", fmtPct(oqa), `${fmtN(block.optins)} / ${fmtN(block.qualifiedApps)}`, oqa);
        push("Qual→Booking", fmtPct(block.qualifiedToBookingRate), `${fmtN(block.qualifiedApps)} / ${fmtN(block.bookedCalls)} qual`, block.qualifiedToBookingRate);
        return rows;
      };

      // Shared rows (webinar info, on-webinar, zoom)
      const sheetRows: SheetRow[] = [];
      sheetRows.push({ section: "Webinar Info", metric: "Audience Type", value: aud === "warm" ? "Warm Traffic" : "Cold Traffic", notes: "" });

      if (enh) {
        const owMetrics: { metric: string; value: string; notes: string; val?: number; isOw?: boolean }[] = [
          { metric: "Opt-ins", value: fmtN(enh.onWebinar.registrants), notes: "" },
          { metric: "CPR", value: enh.onWebinar.cpr > 0 ? `$${enh.onWebinar.cpr.toFixed(2)}` : "—", notes: "Ad spend ÷ Paid opt-ins" },
          { metric: "Attended", value: fmtN(enh.onWebinar.attended), notes: "", val: enh.onWebinar.attended, isOw: true },
          { metric: "Show Rate", value: fmtPct(enh.onWebinar.showRate), notes: `${fmtN(enh.onWebinar.attended)} / ${fmtN(enh.onWebinar.registrants)}`, val: enh.onWebinar.showRate, isOw: true },
          { metric: "Avg Duration", value: `${Math.round(enh.onWebinar.avgDuration)}m`, notes: "", val: enh.onWebinar.avgDuration, isOw: true },
          ...(enh.onWebinar.pitchMinute ? [
            { metric: "Pitch Start", value: `${enh.onWebinar.pitchMinute}m`, notes: "", val: enh.onWebinar.pitchMinute, isOw: true },
            { metric: "@ Pitch", value: fmtPct(enh.onWebinar.retentionAtPitch), notes: fmtN(enh.onWebinar.attendeesAtPitch), val: enh.onWebinar.retentionAtPitch, isOw: true },
            { metric: "Booking Rate", value: fmtPct(enh.onWebinar.attendeesAtPitch > 0 ? (enh.salesOnWebby.bookedCalls / enh.onWebinar.attendeesAtPitch) * 100 : 0), notes: `${fmtN(enh.salesOnWebby.bookedCalls)} on-webby booked / ${fmtN(enh.onWebinar.attendeesAtPitch)} at pitch`, val: enh.onWebinar.attendeesAtPitch > 0 ? (enh.salesOnWebby.bookedCalls / enh.onWebinar.attendeesAtPitch) * 100 : 0, isOw: true },
          ] : [{ metric: "Booking Rate", value: fmtPct(enh.onWebinar.bookingRate), notes: "", val: enh.onWebinar.bookingRate, isOw: true }]),
          ...(enh.onWebinar.attendeesAtPitch > 0 ? [
            { metric: "Total App Rate", value: fmtPct(enh.salesOnWebby.apps / enh.onWebinar.attendeesAtPitch * 100), notes: `${fmtN(enh.salesOnWebby.apps)} on-webby apps ÷ ${fmtN(enh.onWebinar.attendeesAtPitch)} at pitch (${fmtN(enh.salesTotal.apps)} total)`, val: enh.salesOnWebby.apps / enh.onWebinar.attendeesAtPitch * 100, isOw: true },
            { metric: "Full App Rate", value: fmtPct(enh.salesOnWebby.fullApps / enh.onWebinar.attendeesAtPitch * 100), notes: `${fmtN(enh.salesOnWebby.fullApps)} on-webby full apps / ${fmtN(enh.onWebinar.attendeesAtPitch)} at pitch`, val: enh.salesOnWebby.fullApps / enh.onWebinar.attendeesAtPitch * 100, isOw: true },
            { metric: "Qual App Rate", value: fmtPct(enh.salesOnWebby.qualifiedApps / enh.onWebinar.attendeesAtPitch * 100), notes: `${fmtN(enh.salesOnWebby.qualifiedApps)} on-webby qualified / ${fmtN(enh.onWebinar.attendeesAtPitch)} at pitch`, val: enh.salesOnWebby.qualifiedApps / enh.onWebinar.attendeesAtPitch * 100, isOw: true },
          ] : []),
        ];
        owMetrics.forEach((r) => {
          const d = r.val !== undefined ? computeDisplay(r.metric, r.val, enh.onWebinar.registrants, r.isOw ?? false) : null;
          sheetRows.push({ section: "On-Webinar Metrics", metric: r.metric, value: r.value, notes: r.notes, target: d?.rangeStr, indicator: d?.indicator });
        });
      }

      if (f.hasZoomData) {
        const timingRows = regTimingCache[id] ?? [];
        const zoomOptins = enh?.onWebinar.registrants ?? f.totalRegistrants;
        const pushZoom = (metric: string, value: string, notes: string, val?: number) => {
          const tgt = val !== undefined ? (() => {
            const entry = ZOOM_TARGETS[metric];
            const t = entry ? (aud === "warm" ? entry.warm : entry.cold) : null;
            return t && Number.isFinite(val) ? targetDisplay(t, val) : null;
          })() : null;
          sheetRows.push({ section: "Zoom Attendance", metric, value, notes, target: tgt?.rangeStr, indicator: tgt?.indicator });
        };
        const zow = enh?.onWebinar;
        pushZoom("Registrants", fmtN(zow ? zow.registrants : f.totalRegistrants), zow ? zoomRawRegLabel(zow) ?? "" : "");
        pushZoom("Attended", fmtN(zow ? zow.attended : f.totalAttended), zow ? zoomRawAttLabel(zow) ?? "" : "", zow ? zow.attended : f.totalAttended);
        pushZoom("Show Rate", fmtPct(zow ? zow.showRate : f.showRate), zow ? `${fmtN(zow.attended)} / ${fmtN(zow.registrants)}` : "", zow ? zow.showRate : f.showRate);
        pushZoom("Avg Duration", `${Math.round(zow ? zow.avgDuration : f.avgDuration)}m`, "", zow ? zow.avgDuration : f.avgDuration);
        timingRows.forEach((p) => pushZoom(p.label, `${p.showRate.toFixed(1)}%`, `${p.attended} showed / ${p.optins} registered`, p.showRate));
      }

      // Full export always includes all 3 sales views
      const exportRows: SheetRow[] = [
        ...sheetRows.filter((r) => r.section === "Webinar Info"),
        ...(totalBlock ? buildBlockRows(totalBlock, "Blended (All) — Sales", enh?.totalEocsTaken ?? 0) : []),
        ...(onBlock ? buildBlockRows(onBlock, "On Webinar — Sales", onBlock.takenCalls) : []),
        ...(postBlock ? buildBlockRows(postBlock, "Post Webinar — Sales", postBlock.takenCalls) : []),
        ...sheetRows.filter((r) => r.section === "On-Webinar Metrics"),
        ...sheetRows.filter((r) => r.section === "Zoom Attendance"),
      ];

      const webinarLabel = `${f.webinarName ?? id} — ${formatDate(def?.date ?? "")}`;

      const downloadCSV = () => {
        const lines = [`"Webinar","${webinarLabel.replace(/"/g, '""')}"`, `"Section","Metric","Value","Target","Notes"`];
        exportRows.forEach((r) => {
          lines.push([r.section, r.metric, r.value, r.target ?? "", r.notes].map((v) => `"${v.replace(/"/g, '""')}"`).join(","));
        });
        const blob = new Blob([lines.join("\n")], { type: "text/csv" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
        a.download = `${id}-metrics.csv`; a.click();
      };

      const downloadExcel = () => {
        const wb = XLSX.utils.book_new();
        const colWidths = [{ wch: 30 }, { wch: 16 }, { wch: 14 }, { wch: 30 }];
        const addSheet = (rows: SheetRow[], sheetName: string, header: string) => {
          const data: string[][] = [
            [header, webinarLabel], [],
            ["Metric", "Value", "Target", "Notes"],
            ...rows.map((r) => [r.metric, r.value, r.target ?? "", r.notes]),
          ];
          const ws = XLSX.utils.aoa_to_sheet(data);
          ws["!cols"] = colWidths;
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        };
        const infoRows = sheetRows.filter((r) => r.section === "Webinar Info");
        if (infoRows.length) addSheet(infoRows, "Info", "Webinar Info");
        const blended = totalBlock ? buildBlockRows(totalBlock, "Blended (All)", enh?.totalEocsTaken ?? 0) : [];
        if (blended.length) addSheet(blended, "Blended", "Blended — All Sales");
        const onWRows = onBlock ? buildBlockRows(onBlock, "On Webinar", onBlock.takenCalls) : [];
        if (onWRows.length) addSheet(onWRows, "On Webinar", "On Webinar Sales");
        const postWRows = postBlock ? buildBlockRows(postBlock, "Post Webinar", postBlock.takenCalls) : [];
        if (postWRows.length) addSheet(postWRows, "Post Webinar", "Post Webinar Sales");
        const owXlsRows = sheetRows.filter((r) => r.section === "On-Webinar Metrics");
        if (owXlsRows.length) addSheet(owXlsRows, "On-Webinar Metrics", "On-Webinar Metrics");
        const zoomXlsRows = sheetRows.filter((r) => r.section === "Zoom Attendance");
        if (zoomXlsRows.length) addSheet(zoomXlsRows, "Zoom Attendance", "Zoom Attendance");
        XLSX.writeFile(wb, `${id}-metrics.xlsx`);
      };

      const downloadPDF = () => {
        const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
        const pageW = doc.internal.pageSize.getWidth();

        // Header
        doc.setFillColor(17, 178, 248); // brand gradient start
        doc.rect(0, 0, pageW, 18, "F");
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.text("Urban Unity — Webinar Report", 14, 8);
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(webinarLabel, 14, 14);

        // Section groups
        const sections: { title: string; rows: SheetRow[] }[] = [
          { title: "Webinar Info", rows: exportRows.filter((r) => r.section === "Webinar Info") },
          { title: "Blended (All) — Sales", rows: exportRows.filter((r) => r.section === "Blended (All) — Sales") },
          { title: "On Webinar — Sales", rows: exportRows.filter((r) => r.section === "On Webinar — Sales") },
          { title: "Post Webinar — Sales", rows: exportRows.filter((r) => r.section === "Post Webinar — Sales") },
          { title: "On-Webinar Metrics", rows: exportRows.filter((r) => r.section === "On-Webinar Metrics") },
          { title: "Zoom Attendance", rows: exportRows.filter((r) => r.section === "Zoom Attendance") },
        ].filter((s) => s.rows.length > 0);

        let startY = 24;
        sections.forEach(({ title, rows }) => {
          autoTable(doc, {
            startY,
            head: [[{ content: title, colSpan: 4, styles: { fillColor: [80, 77, 249], textColor: 255, fontStyle: "bold", fontSize: 9 } }],
                   [{ content: "Metric", styles: { fillColor: [240, 240, 255], textColor: [40, 40, 80], fontStyle: "bold", fontSize: 8 } },
                    { content: "Value",  styles: { fillColor: [240, 240, 255], textColor: [40, 40, 80], fontStyle: "bold", fontSize: 8 } },
                    { content: "Target", styles: { fillColor: [240, 240, 255], textColor: [40, 40, 80], fontStyle: "bold", fontSize: 8 } },
                    { content: "Notes",  styles: { fillColor: [240, 240, 255], textColor: [40, 40, 80], fontStyle: "bold", fontSize: 8 } }]],
            body: rows.map((r) => [r.metric, r.value, r.target ?? "—", r.notes]),
            columns: [{ dataKey: 0 }, { dataKey: 1 }, { dataKey: 2 }, { dataKey: 3 }],
            columnStyles: {
              0: { cellWidth: 68, fontSize: 8 },
              1: { cellWidth: 28, fontSize: 8, halign: "center" },
              2: { cellWidth: 36, fontSize: 8, halign: "center" },
              3: { cellWidth: 50, fontSize: 8 },
            },
            styles: { cellPadding: 2, fontSize: 8, lineColor: [220, 220, 235], lineWidth: 0.2 },
            alternateRowStyles: { fillColor: [248, 248, 255] },
            margin: { left: 8, right: 8 },
            didDrawPage: (data) => { startY = data.cursor?.y ?? startY; },
          });
          // @ts-expect-error lastAutoTable is added by jspdf-autotable
          startY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
        });

        doc.save(`${id}-metrics.pdf`);
      };

      // Display rows = active metrics view (respects metricsView toggle)
      const displayBlock = metricsView === "on-webby" ? onBlock : metricsView === "post-webby" ? postBlock : totalBlock;
      const displayLabel = metricsView === "on-webby" ? "On Webinar — Sales" : metricsView === "post-webby" ? "Post Webinar — Sales" : "Blended (All) — Sales";
      const displayTaken = metricsView === "on-webby" ? (onBlock?.takenCalls ?? 0) : metricsView === "post-webby" ? (postBlock?.takenCalls ?? 0) : (enh?.totalEocsTaken ?? 0);
      const displayRows: SheetRow[] = [
        ...sheetRows.filter((r) => r.section === "Webinar Info"),
        ...(displayBlock ? buildBlockRows(displayBlock, displayLabel, displayTaken) : []),
        ...sheetRows.filter((r) => r.section === "On-Webinar Metrics"),
        ...sheetRows.filter((r) => r.section === "Zoom Attendance"),
      ];

      const sections: { title: string; rows: SheetRow[] }[] = [];
      displayRows.forEach((r) => {
        const last = sections[sections.length - 1];
        if (last && last.title === r.section) last.rows.push(r);
        else sections.push({ title: r.section, rows: [r] });
      });

      let globalRowNum = 1;

      return (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-bold text-foreground">{f.webinarName ?? formatDate(def?.date ?? "")}</h2>
              <p className="text-[10px] text-muted-foreground">{formatDate(f.webinarDate)}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {enh && (
                <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
                  {(["total", "on-webby", "post-webby"] as const).map((v) => (
                    <button key={v} onClick={() => setMetricsView(v)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${metricsView === v ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`}>
                      {v === "total" ? "All" : v === "on-webby" ? "On Webinar" : "Post Webinar"}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={downloadCSV}
                className="flex items-center gap-1 px-2.5 py-1 rounded border border-border bg-card text-[11px] font-medium text-muted-foreground hover:text-foreground hover:border-[var(--brand-primary)]/50 transition-colors">
                ↓ CSV
              </button>
              <button onClick={downloadExcel}
                className="flex items-center gap-1 px-2.5 py-1 rounded border border-border bg-card text-[11px] font-medium text-muted-foreground hover:text-foreground hover:border-[var(--brand-primary)]/50 transition-colors">
                ↓ Excel
              </button>
              <button onClick={downloadPDF}
                className="flex items-center gap-1 px-2.5 py-1 rounded border border-border bg-card text-[11px] font-medium text-muted-foreground hover:text-foreground hover:border-[var(--brand-primary)]/50 transition-colors">
                ↓ PDF
              </button>
            </div>
          </div>

          <div className="overflow-auto rounded-lg shadow-sm" style={{ border: "1px solid #c6c6c6" }}>
            <table className="w-full text-[12px] border-collapse" style={{ fontFamily: "system-ui, sans-serif", backgroundColor: "#ffffff" }}>
              <thead>
                <tr style={{ background: `linear-gradient(90deg, ${branding.colors.gradient.start} 0%, ${branding.colors.gradient.end} 100%)` }}>
                  <th style={{ width: 32, padding: "6px 8px", textAlign: "right", fontSize: 10, fontWeight: 400, color: "rgba(255,255,255,0.5)", borderRight: "1px solid rgba(255,255,255,0.15)" }} className="select-none"></th>
                  <th style={{ padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#ffffff", letterSpacing: "0.06em", borderRight: "1px solid rgba(255,255,255,0.15)" }}>Metric</th>
                  <th style={{ width: 140, padding: "6px 12px", textAlign: "right", fontSize: 11, fontWeight: 700, color: "#ffffff", letterSpacing: "0.06em", borderRight: "1px solid rgba(255,255,255,0.15)" }}>Value</th>
                  <th style={{ width: 160, padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#ffffff", letterSpacing: "0.06em", borderRight: "1px solid rgba(255,255,255,0.15)" }}>Target</th>
                  <th style={{ width: 220, padding: "6px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.8)", letterSpacing: "0.06em" }}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {sections.map((sec) => (
                  <>
                    <tr key={`sec-${sec.title}`} style={{ background: `linear-gradient(90deg, ${branding.colors.gradient.start} 0%, ${branding.colors.gradient.end} 100%)` }}>
                      <td style={{ padding: "4px 8px", borderRight: "1px solid rgba(255,255,255,0.15)", borderBottom: "1px solid rgba(255,255,255,0.1)" }} className="select-none"></td>
                      <td colSpan={4} style={{ padding: "4px 12px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#ffffff", textTransform: "uppercase", letterSpacing: "0.1em" }}>{sec.title}</span>
                      </td>
                    </tr>
                    {sec.rows.map((row, ri) => {
                      const rn = globalRowNum++;
                      const isEven = ri % 2 === 0;
                      const indColor = row.indicator === "above" ? "#22c55e" : row.indicator === "below" ? "#ef4444" : "#eab308";
                      return (
                        <tr key={`${sec.title}-${ri}`}
                          style={{ backgroundColor: isEven ? "#ffffff" : "#f8f9fa" }}
                          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e8f0fe")}
                          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isEven ? "#ffffff" : "#f8f9fa")}>
                          <td style={{ padding: "3px 8px", textAlign: "right", fontSize: 10, color: "#80868b", backgroundColor: "#f1f3f4", border: "1px solid #d3d3d3", width: 32, userSelect: "none" }}>{rn}</td>
                          <td style={{ padding: "3px 12px", border: "1px solid #d3d3d3", color: "#202124" }}>{row.metric}</td>
                          <td style={{ padding: "3px 12px", textAlign: "right", border: "1px solid #d3d3d3", color: "#202124", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{row.value || "—"}</td>
                          <td style={{ padding: "4px 12px", border: "1px solid #d3d3d3" }}>
                            {row.target && row.indicator ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                {makeTrendIcon(row.indicator, indColor)}
                                <span style={{ fontSize: 11, fontWeight: 600, color: indColor }}>{row.target}</span>
                              </div>
                            ) : null}
                          </td>
                          <td style={{ padding: "3px 12px", border: "1px solid #d3d3d3", color: "#5f6368", fontSize: 11 }}>{row.notes}</td>
                        </tr>
                      );
                    })}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        {/* Header with name, date, badges, ad spend — outside card for clean screenshots */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className={`${isCompare ? "text-sm" : "text-base"} font-bold text-foreground`}>{f.webinarName ?? formatDate(def?.date ?? "")}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{formatDate(f.webinarDate)}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {!f.hasZoomData && (
              <span className="text-[10px] bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded px-2 py-0.5">No Zoom data</span>
            )}
            {aud && (
              <span className={`text-[10px] font-semibold border rounded px-2 py-0.5 ${
                aud === "warm"
                  ? "bg-orange-500/10 text-orange-400 border-orange-500/20"
                  : "bg-blue-500/10 text-blue-400 border-blue-500/20"
              }`}>{aud === "warm" ? "Warm Traffic" : "Cold Traffic"}</span>
            )}
            {!isCompare && (
              <>
                <button onClick={() => setUploadModal(id)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded border border-border bg-card text-[10px] font-medium text-muted-foreground hover:text-foreground transition-colors">
                  ↑ Zoom CSV
                </button>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground">Ad Spend $</span>
                  <input type="number" min="0" value={adSpendInput[id] ?? ""} onChange={(e) => setAdSpendInput((prev) => ({ ...prev, [id]: e.target.value }))} placeholder="0"
                    className="w-20 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                  <button onClick={() => saveAdSpend(id)} disabled={savingAdSpend === id}
                    className="px-2 py-0.5 text-[10px] bg-[var(--brand-primary)] text-white rounded disabled:opacity-50">
                    {savingAdSpend === id ? "…" : "Save"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Sales metrics card — clean for screenshots */}
        <div className="bg-card border border-border rounded-xl p-4">
          {/* Metrics perspective toggle — Total / On Webby / Post Webby */}
          {enh && (
            <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Sales Metrics</h3>
                {hiddenMetrics.size > 0 && (
                  <button onClick={() => setHiddenMetrics(new Set())}
                    className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border border-[var(--brand-primary)]/20 hover:bg-[var(--brand-primary)]/20 transition-colors">
                    ↺ Reset ({hiddenMetrics.size})
                  </button>
                )}
              </div>
              <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
                {(["total", "on-webby", "post-webby"] as const).map((v) => (
                  <button key={v} onClick={() => setMetricsView(v)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                      metricsView === v ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                    }`}>
                    {v === "total" ? "Total" : v === "on-webby" ? "On Webby" : "Post Webby"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sales scorecards with highlights + target badges */}
          <div className={`grid gap-3 ${isCompare ? "grid-cols-3 md:grid-cols-4" : "grid-cols-3 md:grid-cols-4 lg:grid-cols-5"}`}>
            {salesKpis.filter((s) => !hiddenMetrics.has(s.l)).map((s, i) => {
              if (s.l === "Missing Outcome") return <MissingOutcomeCard key={s.l} webinarId={id} traffic={traffic} view={metricsView} compact={isCompare} onHide={() => toggleMetric(s.l)} />;
              const showBar = metricsView === "total" && !s.isRate && s.totalVal !== undefined && s.onVal !== undefined && s.postVal !== undefined && s.totalVal > 0;
              const onPct = showBar ? (s.onVal! / s.totalVal!) * 100 : 0;
              const canClick = s.clickable && STAGE_KEY_MAP[s.l];
              const highlightKey = `${id}-${s.l}`;
              const highlight = showHighlights ? cardHighlights[highlightKey] : undefined;
              // Volume targets (counts, Cash) are a whole webinar's weekly target, so they only score the Total view.
              const target0 = resolveTarget(s.l, aud, false);
              const target = target0 && (metricsView === "total" || !(target0.unit === "n" || s.l === "Cash")) ? target0 : null;
              const otherCompareId = isCompare ? selectedIds.find((sid) => sid !== id) ?? null : null;
              const prevEnh = otherCompareId ? enhancedData[`${otherCompareId}-${traffic}-${appTypeFilter}`] ?? null : null;
              const prevBlock = prevEnh ? (metricsView === "on-webby" ? prevEnh.salesOnWebby : metricsView === "post-webby" ? prevEnh.salesPostWebby : prevEnh.salesTotal) : null;

              return (
                <motion.div key={s.l} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  onClick={canClick ? () => handleStageClick(id, s.l, traffic as "all" | "paid" | "organic") : undefined}
                  title={s.tip}
                  className={`relative group rounded-lg px-3 py-3 border transition-colors ${highlight === "green" ? "bg-green-500/10 border-green-500/40" : highlight === "red" ? "bg-red-500/10 border-red-500/40" : "bg-muted/20 border-border/40"} ${canClick ? "cursor-pointer hover:border-[var(--brand-primary)]/40 hover:bg-muted/30" : ""}`}>
                  <button onClick={(e) => { e.stopPropagation(); toggleMetric(s.l); }}
                    className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded-full text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/40 opacity-0 group-hover:opacity-100 transition-all text-[10px] leading-none"
                    title="Hide metric">×</button>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">{s.dl ?? s.l}{canClick ? " ↗" : ""}{s.tip ? <span className="ml-1 normal-case opacity-70" aria-label={s.tip}>ⓘ</span> : null}</div>
                  <div className={`${isCompare ? "text-base" : "text-xl"} font-bold text-foreground tabular-nums`}>{s.v}</div>
                  {"subCount" in s && s.subCount && !isCompare && (
                    <div className="text-[10px] text-muted-foreground/60 tabular-nums mt-0.5">{s.subCount}</div>
                  )}
                  {/* Target badge — single view only */}
                  {!isCompare && target && s.num !== undefined && (
                    <div className="mt-1">{renderTargetBadge(s.num, target, isCompare, enh?.onWebinar.attendeesAtPitch || activeBlock?.optins || 0)}</div>
                  )}
                  {/* Compare delta */}
                  {isCompare && showHighlights && prevBlock && s.num !== undefined && (
                    <div className="mt-1">{renderMetricDelta(s.num, (() => {
                      if (s.l === "Opt-ins") return prevBlock.optins;
                      if (s.l === "Full Apps") return prevBlock.fullApps;
                      if (s.l === "Qualified Apps") return prevBlock.qualifiedApps;
                      if (s.l === "Calls Booked") return prevBlock.bookedCalls;
                      if (s.l === "Calls Taken") return prevBlock.takenCalls;
                      if (s.l === "No Shows") return prevBlock.noShows;
                      if (s.l === "Show Rate") return prevBlock.showRate;
                      if (s.l === "Closes") return prevBlock.closed;
                      if (s.l === "Deposits") return prevBlock.deposits;
                      if (s.l === "Close Rate") return prevBlock.closeRate;
                      if (s.l === "Cash") return prevBlock.cash;
                      if (s.l === "AOV") return prevBlock.auv;
                      if (s.l === "Cash/Call") return prevBlock.cashPerCall;
                      if (s.l === "Full App→Booking") return prevBlock.appToBookingRate;
                      if (s.l === "Cancelled Calls") return (prevBlock.cancelledBySales + prevBlock.cancelledByLead);
                      return 0;
                    })(), s.lowerIsBetter)}</div>
                  )}
                  {/* Proportion bar */}
                  {showBar && (
                    <div className="mt-2">
                      <div className="flex items-center gap-1.5 h-1.5 w-full rounded-full overflow-hidden bg-muted/30">
                        <div className="h-full rounded-full bg-[var(--brand-gradient-start)]" style={{ width: `${onPct}%` }} />
                        <div className="h-full rounded-full bg-[var(--brand-accent)] opacity-60" style={{ width: `${100 - onPct}%`, marginLeft: "-6px" }} />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[9px] text-muted-foreground">{s.isCash ? fmt$(s.onVal!) : fmtN(s.onVal!)} on</span>
                        <span className="text-[9px] text-muted-foreground">{s.isCash ? fmt$(s.postVal!) : fmtN(s.postVal!)} post</span>
                      </div>
                    </div>
                  )}
                  {/* Highlight toggle buttons */}
                  {showHighlights && (
                    <div className="flex items-center gap-1 mt-1.5">
                      {highlight && (
                        <button onClick={(e) => { e.stopPropagation(); clearCardHighlight(id, s.l); }}
                          className="text-[9px] text-muted-foreground/50 hover:text-muted-foreground leading-none">✕</button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); toggleCardHighlight(id, s.l, "green"); }}
                        className={`w-3 h-3 rounded-full bg-green-500 transition-opacity ${highlight === "green" ? "opacity-100 ring-1 ring-white/40" : "opacity-30 hover:opacity-70"}`} />
                      <button onClick={(e) => { e.stopPropagation(); toggleCardHighlight(id, s.l, "red"); }}
                        className={`w-3 h-3 rounded-full bg-red-500 transition-opacity ${highlight === "red" ? "opacity-100 ring-1 ring-white/40" : "opacity-30 hover:opacity-70"}`} />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
          {/* Engagement metrics input (non-compare only) */}
          {!isCompare && enh && (
            <div className="flex items-center gap-3 pt-3 border-t border-border/30 flex-wrap">
              <span className="text-[10px] text-muted-foreground">Add to Calendar</span>
              <input type="number" min="0" placeholder={enh.addToCalendar > 0 ? String(enh.addToCalendar) : "0"}
                value={engagementInput[id]?.addToCalendar ?? ""}
                onChange={(e) => setEngagementInput((prev) => ({ ...prev, [id]: { ...prev[id], addToCalendar: e.target.value } }))}
                className="w-16 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
              <span className="text-[10px] text-muted-foreground">Joined WhatsApp</span>
              <input type="number" min="0" placeholder={enh.joinedWhatsApp > 0 ? String(enh.joinedWhatsApp) : "0"}
                value={engagementInput[id]?.joinedWhatsApp ?? ""}
                onChange={(e) => setEngagementInput((prev) => ({ ...prev, [id]: { ...prev[id], joinedWhatsApp: e.target.value } }))}
                className="w-16 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
              <button onClick={() => saveEngagement(id)} disabled={savingEngagement === id}
                className="px-2 py-0.5 text-[10px] bg-[var(--brand-primary)] text-white rounded disabled:opacity-50">
                {savingEngagement === id ? "…" : "Set"}
              </button>
            </div>
          )}
        </div>

        {/* Funnel chart */}
        <div className="bg-card border border-border rounded-xl p-4 md:p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-foreground">Funnel</h3>
            {!isCompare && (
              <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
                {(["all", "compare"] as const).map((mode) => (
                  <button key={mode} onClick={() => setFunnelMode(mode)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                      funnelMode === mode ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                    }`}>{mode}</button>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-muted-foreground mb-4">Click any block to view leads</p>
          {!isCompare && funnelMode === "compare" ? (
            <div className="flex gap-3 md:gap-6">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-center text-muted-foreground mb-3 uppercase tracking-wider">Organic</p>
                <FunnelChart stages={compareOrganicStages} gradientId="fgOrganic" onStageClick={(label) => handleStageClick(id, label, "organic")} />
              </div>
              <div className="w-px bg-border/30 self-stretch" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-center text-muted-foreground mb-3 uppercase tracking-wider">Paid</p>
                <FunnelChart stages={comparePaidStages} gradientId="fgPaid" onStageClick={(label) => handleStageClick(id, label, "paid")} />
              </div>
            </div>
          ) : (
            <FunnelChart stages={stages} gradientId={`fg${id}`} onStageClick={(label) => handleStageClick(id, label, isCompare ? traffic : "all")} />
          )}
        </div>

        {/* On-Webinar Metrics */}
        {enh && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-sm font-semibold text-foreground">On-Webinar Metrics</h3>
              {hiddenOnWebinarMetrics.size > 0 && (
                <button onClick={() => setHiddenOnWebinarMetrics(new Set())}
                  className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] border border-[var(--brand-primary)]/20 hover:bg-[var(--brand-primary)]/20 transition-colors">
                  ↺ Reset ({hiddenOnWebinarMetrics.size})
                </button>
              )}
            </div>
            <div className={`grid ${isCompare ? "grid-cols-2 md:grid-cols-3" : "grid-cols-2 md:grid-cols-4"} gap-3 mb-4`}>
              {[
                { l: "OPT-INS", v: fmtN(enh.onWebinar.registrants), num: enh.onWebinar.registrants, subCount: `to ${fmtOptinCutoff(enh.onWebinar.optinCutoff)} (same as Sales Metrics)` },
                { l: "CPR", dl: LBL.cpr, v: fmtWebinarMetric("cpr", smT?.cpr), num: smT?.cpr ?? 0, subCount: smT ? webinarMetricHow("cpr", smT) : undefined, tip: "Cost per registrant = ad spend ÷ Paid opt-ins (the same count as Callix's Ads Manager), on All and Paid. Organic has no ad spend." },
                { l: "Attended", v: fmtN(enh.onWebinar.attended), num: enh.onWebinar.attended },
                { l: "Show Rate", dl: LBL.webinarShowRate, v: fmtWebinarMetric("webinarShowRate", smT?.webinarShowRate), num: smT?.webinarShowRate ?? 0, subCount: smT ? webinarMetricHow("webinarShowRate", smT) : undefined },
                { l: "Avg Duration", v: `${Math.round(enh.onWebinar.avgDuration)}m`, num: enh.onWebinar.avgDuration },
                ...(enh.onWebinar.pitchMinute ? [
                  { l: "Pitch Start", v: `${enh.onWebinar.pitchMinute}m`, num: enh.onWebinar.pitchMinute },
                  { l: "@ Pitch", dl: LBL.retention, v: fmtWebinarMetric("retention", smT?.retention), num: enh.onWebinar.retentionAtPitch, subCount: `${fmtN(enh.onWebinar.attendeesAtPitch)} ${LBL.atPitch.toLowerCase()}` },
                ] : []),
                // Booking / application rates = ON WEBBY bookings / applications only (applications submitted while the webinar was live, and
                // those applicants' bookings) ÷ people at pitch, whatever the Total / On Webby / Post Webby toggle shows (Joey, Nov 2 2026: "only
                // and always be on-webinar metrics, not blended"). The whole-webinar totals stay in Sales Metrics (Total view).
                { l: "Booking Rate", dl: LBL.bookingRate, v: fmtWebinarMetric("bookingRate", smOW?.bookingRate), num: smOW?.bookingRate ?? 0, subCount: smOW ? `${webinarMetricHow("bookingRate", smOW)} · on webby only (${fmtN(enh.salesTotal.bookedCalls)} total)` : undefined },
                ...(enh.onWebinar.attendeesAtPitch > 0 ? [
                  { l: "Total App Rate", dl: LBL.appRate, v: fmtWebinarMetric("appRate", smOW?.appRate), num: smOW?.appRate ?? 0, subCount: smOW ? `${webinarMetricHow("appRate", smOW)} · on webby only (${fmtN(enh.salesTotal.apps)} total)` : undefined },
                  { l: "Full App Rate", v: fmtPct((enh.salesOnWebby.fullApps / enh.onWebinar.attendeesAtPitch) * 100), num: (enh.salesOnWebby.fullApps / enh.onWebinar.attendeesAtPitch) * 100, subCount: `${fmtN(enh.salesOnWebby.fullApps)} full apps ÷ ${fmtN(enh.onWebinar.attendeesAtPitch)} at pitch · on webby only` },
                  { l: "Qual App Rate", v: fmtPct((enh.salesOnWebby.qualifiedApps / enh.onWebinar.attendeesAtPitch) * 100), num: (enh.salesOnWebby.qualifiedApps / enh.onWebinar.attendeesAtPitch) * 100, subCount: `${fmtN(enh.salesOnWebby.qualifiedApps)} qualified ÷ ${fmtN(enh.onWebinar.attendeesAtPitch)} at pitch · on webby only` },
                ] : []),
                ...(enh.dials.totalDials > 0 ? [
                  { l: "Total Dials", v: fmtN(enh.dials.totalDials), num: enh.dials.totalDials },
                  { l: "Pickup Rate", v: fmtPct(enh.dials.pickupRate), num: enh.dials.pickupRate },
                ] : []),
              ].filter((s) => !hiddenOnWebinarMetrics.has(s.l)).map((s, i) => {
                const owTarget = resolveTarget(s.l, aud, true);
                return (
                  <motion.div key={s.l} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    className="relative group bg-muted/20 border border-border/40 rounded-lg px-3 py-3">
                    <button onClick={() => toggleOnWebinarMetric(s.l)}
                      className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center rounded-full text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/40 opacity-0 group-hover:opacity-100 transition-all text-[10px] leading-none"
                      title="Hide metric">×</button>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">{"dl" in s && s.dl ? s.dl : s.l}</div>
                    <div className={`${isCompare ? "text-base" : "text-xl"} font-bold text-foreground tabular-nums`}>{s.v}</div>
                    {"subCount" in s && s.subCount && (
                      <div className="text-[10px] text-muted-foreground/60 tabular-nums mt-0.5">{s.subCount}</div>
                    )}
                    {!isCompare && owTarget && s.num !== undefined && <div className="mt-1">{renderTargetBadge(s.num, owTarget, isCompare, activeBlock?.optins ?? 0)}</div>}
                  </motion.div>
                );
              })}
            </div>
            {!isCompare && (
              <div className="flex items-center gap-2 pt-3 border-t border-border/30">
                <span className="text-[10px] text-muted-foreground">Pitch starts at minute</span>
                <input type="number" min="0" placeholder="e.g. 45" value={pitchMinuteInput[id] ?? ""}
                  onChange={(e) => setPitchMinuteInput((prev) => ({ ...prev, [id]: e.target.value }))}
                  className="w-16 px-1.5 py-0.5 text-xs bg-background border border-border rounded text-foreground focus:outline-none focus:border-[var(--brand-primary)]" />
                <button onClick={() => savePitchMinute(id)} disabled={savingPitchMinute === id}
                  className="px-2 py-0.5 text-[10px] bg-[var(--brand-primary)] text-white rounded disabled:opacity-50">
                  {savingPitchMinute === id ? "…" : "Set"}
                </button>
              </div>
            )}
          </div>
        )}


        {/* Zoom Attendance + Retention */}
        {f.hasZoomData && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Zoom Attendance</h3>
            <div className={`grid ${isCompare ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4"} gap-4 mb-5`}>
              {[
                // Headline = the capped opt-ins / on-time attendees (same as On-Webinar Metrics); Zoom's raw totals as the grey line.
                { l: "Registrants",  v: fmtN(enh ? enh.onWebinar.registrants : f.totalRegistrants), sub: enh ? zoomRawRegLabel(enh.onWebinar) : undefined },
                { l: "Attended",     v: fmtN(enh ? enh.onWebinar.attended : f.totalAttended), sub: enh ? zoomRawAttLabel(enh.onWebinar) : undefined },
                { l: "Show Rate",    v: fmtPct(enh ? enh.onWebinar.showRate : f.showRate), sub: enh ? `${fmtN(enh.onWebinar.attended)} / ${fmtN(enh.onWebinar.registrants)}` : undefined },
                { l: "Avg Duration", v: `${Math.round(enh ? enh.onWebinar.avgDuration : f.avgDuration)}m`, sub: undefined as string | undefined },
              ].map((s, i) => (
                <motion.div key={s.l} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}>
                  <div className={`${isCompare ? "text-sm" : "text-lg"} font-bold text-foreground`}>{s.v}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{s.l}</div>
                  {s.sub && <div className="text-[10px] text-muted-foreground/60 tabular-nums mt-0.5">{s.sub}</div>}
                </motion.div>
              ))}
            </div>
            {ret.length > 0 && (
              <>
                <p className="text-xs text-muted-foreground mb-3">Audience retention — % still watching by minute</p>
                <ResponsiveContainer width="100%" height={isCompare ? 140 : 180}>
                  <AreaChart data={ret} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id={`retGrad${id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={branding.colors.gradient.start} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={branding.colors.gradient.start} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="minute" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${v}m`} />
                    <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${Math.round(v as number)}%`} domain={[0, 100]} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                      labelFormatter={(v) => `${v} min`} formatter={(_v: number, _name: string, item: { payload: RetentionPoint }) => [String(item.payload.count), "Still watching"]} />
                    <Area type="monotone" dataKey="pct" stroke={branding.colors.gradient.start} strokeWidth={2} fill={`url(#retGrad${id})`} />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            )}
            {/* Retention by source */}
            {retSources.length > 1 && (
              <div className="mt-5 pt-4 border-t border-border/30">
                <RetentionBySourceChart data={retBySrc} sources={retSources} colors={branding.colors.chart} />
              </div>
            )}
          </div>
        )}

        {/* Front-End Low Ticket: not on Cr8Her's Webinars tab, removed for the 1:1 layout (Joey, Oct 29 2026) */}

        {/* Cash by source + Opt-ins by Platform */}
        {(cashBySrc.length > 0 || optinsByUtmCache[id]) && (
          <div className={`bg-card border border-border rounded-xl p-4 md:p-5 grid gap-8 ${isCompare ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}>
            {cashBySrc.length > 0 && <CashBySourceChart data={cashBySrc} />}
            {optinsByUtmCache[id] && optinsByUtmCache[id].bySource.length > 0 && (
              <OptinsByUtmPieChart data={optinsByUtmCache[id].bySource} title="Opt-ins by Platform (UTM Source)" />
            )}
          </div>
        )}

        {/* Registration Timing vs Show Rate */}
        {regTimingCache[id] && regTimingCache[id].length > 1 && (
          <div className="bg-card border border-border rounded-xl p-4 md:p-5">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-xs font-bold tracking-widest uppercase text-white">Registration Timing vs Show Rate</h3>
              <span className="text-xs text-muted-foreground">Show Rate</span>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Show rate by when registrants signed up (earliest → day of)</p>
            <ResponsiveContainer width="100%" height={isCompare ? 140 : 200}>
              <AreaChart data={regTimingCache[id]} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id={`regTimingFill${id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={branding.colors.secondary} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={branding.colors.secondary} stopOpacity={0} />
                  </linearGradient>
                  <filter id={`regTimingGlow${id}`} x="-20%" y="-40%" width="140%" height="180%">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.35)" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.35)" }} tickFormatter={(v) => `${Math.round(v as number)}%`} domain={[0, 100]} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                  formatter={(_v: number, _name: string, item: { payload: { attended: number; optins: number; showRate: number } }) => [
                    `${item.payload.attended} / ${item.payload.optins} (${item.payload.showRate.toFixed(1)}%)`,
                    "Showed",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="showRate"
                  stroke={branding.colors.secondary}
                  strokeWidth={2.5}
                  fill={`url(#regTimingFill${id})`}
                  dot={false}
                  activeDot={{ r: 4, fill: branding.colors.secondary, strokeWidth: 0 }}
                  filter={`url(#regTimingGlow${id})`}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Call Show Rate — Day of Webinar Through Day +5 */}
        {enh && enh.callShowRateByDay && enh.callShowRateByDay.some((d) => d.eocsDue > 0) && (
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="mb-1">
              <h3 className="text-sm font-semibold text-foreground">Call Show Rate — Day of Webinar Through Day +5</h3>
              <p className="text-xs text-muted-foreground mt-0.5">EOC-based show rate (calls taken ÷ calls due) by day relative to the webinar. Shows where show rates dip as days pass.</p>
            </div>
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={enh.callShowRateByDay.filter((d) => d.eocsDue > 0)} margin={{ top: 8, right: 8, left: -16, bottom: 0 }} barCategoryGap="28%">
                  <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "rgba(255,255,255,0.4)" }} tickFormatter={(v) => `${Math.round(v as number)}%`} domain={[0, 100]} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                    formatter={(_v: number, _name: string, item: { payload: { eocsDue: number; eocsTaken: number; showRate: number } }) => [
                      `${item.payload.eocsTaken} / ${item.payload.eocsDue} calls due (${item.payload.showRate.toFixed(1)}%)`,
                      "Show Rate",
                    ]}
                  />
                  <Bar dataKey="showRate" radius={[4, 4, 0, 0]} maxBarSize={56}>
                    {enh.callShowRateByDay.filter((d) => d.eocsDue > 0).map((entry) => (
                      <Cell key={entry.dayOffset} fill={
                        entry.showRate >= 70 ? "var(--brand-primary)" :
                        entry.showRate >= 50 ? "#f59e0b" :
                        "#ef4444"
                      } />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Day-by-day breakdown cards */}
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-6">
              {enh.callShowRateByDay.map((d) => (
                <div key={d.dayOffset} className={`rounded-lg border px-2 py-2 text-center ${
                  d.eocsDue === 0 ? "border-border/30 opacity-40" :
                  d.showRate >= 70 ? "border-[var(--brand-primary)]/40 bg-[var(--brand-primary)]/5" :
                  d.showRate >= 50 ? "border-yellow-500/40 bg-yellow-500/5" :
                  "border-red-500/40 bg-red-500/5"
                }`}>
                  <p className="text-[10px] font-medium text-muted-foreground">{d.label}</p>
                  <p className={`mt-0.5 text-base font-bold tabular-nums ${
                    d.eocsDue === 0 ? "text-muted-foreground" :
                    d.showRate >= 70 ? "text-[var(--brand-primary)]" :
                    d.showRate >= 50 ? "text-yellow-400" :
                    "text-red-400"
                  }`}>{d.eocsDue === 0 ? "—" : `${Math.round(d.showRate)}%`}</p>
                  <p className="text-[9px] text-muted-foreground">{d.eocsDue === 0 ? "no calls" : `${d.eocsTaken}/${d.eocsDue}`}</p>
                </div>
              ))}
            </div>
          </div>
        )}


        {/* Enhanced loading indicator */}
        {enhLoading && (
          <div className="bg-card border border-border rounded-xl p-8 flex items-center justify-center">
            <div className="text-muted-foreground text-sm">Loading enhanced metrics…</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Webinars</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {selectedIds.length > 1 ? `Comparing ${selectedIds.length} webinars` : "Funnel performance by event"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
            <button onClick={() => setViewMode("card")}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === "card" ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`}>
              ⊞ Cards
            </button>
            <button onClick={() => setViewMode("list")}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === "list" ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"}`}>
              ≡ List
            </button>
          </div>
          <div className="flex items-center gap-0.5 rounded-lg border border-border bg-card p-0.5">
            <span className="px-1.5 text-[10px] font-medium text-muted-foreground">All-time blended</span>
            <button onClick={() => downloadBlendedWebinarExport("csv")} disabled={!!exportingBlendedWebinars}
              className="rounded px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50">
              {exportingBlendedWebinars === "csv" ? "Preparing…" : "↓ CSV"}
            </button>
            <button onClick={() => downloadBlendedWebinarExport("xlsx")} disabled={!!exportingBlendedWebinars}
              className="rounded px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50">
              {exportingBlendedWebinars === "xlsx" ? "Preparing…" : "↓ Excel"}
            </button>
            <button onClick={() => downloadBlendedWebinarExport("pdf")} disabled={!!exportingBlendedWebinars}
              className="rounded px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50">
              {exportingBlendedWebinars === "pdf" ? "Preparing…" : "↓ PDF"}
            </button>
          </div>
          <button onClick={() => setAddWebinarModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--brand-primary)] text-white text-xs font-medium hover:opacity-90 transition-opacity">
            + Add Webinar
          </button>
        </div>
      </div>

      {/* Webinar selector */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {webinars.map((w) => (
          <button key={w.id} onClick={() => toggleSelect(w.id)}
            className={`flex-shrink-0 px-3 py-2 rounded-xl border text-left transition-all ${
              selectedIds.includes(w.id)
                ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10"
                : "border-border bg-card hover:border-border/70"
            }`}>
            <div className={`text-xs font-bold whitespace-nowrap ${selectedIds.includes(w.id) ? "text-[var(--brand-primary)]" : "text-foreground"}`}>
              {formatDate(w.date)}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{w.id}</div>
          </button>
        ))}
      </div>

      {/* Split by traffic filter — under webinar selector */}
      {primaryId && (
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Split by traffic:</span>
            <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
              {(["all", "organic", "paid"] as const).map((t) => (
                <button key={t} onClick={() => { setTrafficFilter(t); if (!isSingleView) { const updated: Record<string, "all" | "paid" | "organic"> = {}; selectedIds.forEach((sid) => { updated[sid] = t; }); setCardTraffic(updated); } }}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                    trafficFilter === t ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                  }`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Apps:</span>
            <div className="flex items-center gap-0.5 bg-muted/40 border border-border rounded-lg p-0.5">
              {(["all", "full", "partial"] as const).map((t) => (
                <button key={t} onClick={() => setAppTypeFilter(t)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                    appTypeFilter === t ? "bg-[var(--brand-primary)] text-white" : "text-muted-foreground hover:text-foreground"
                  }`}>{t}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SINGLE WEBINAR VIEW ──────────────────────────────────────────────── */}
      {isSingleView && primaryId && renderWebinarContent(primaryId)}
      {/* Ads Run for This Webinar: campaigns → ads, each with its own Webinar Actuals (Joey, Nov 3 2026) */}
      {isSingleView && primaryId && <div className="mt-6"><WebinarAdsBreakdown webinarId={primaryId} /></div>}
      {isSingleView && !primaryId && (
        <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">Select a webinar</div>
      )}

      {/* ── MULTI-WEBINAR COMPARISON VIEW ────────────────────────────────────── */}
      {!isSingleView && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {selectedIds.map((id, idx) => (
            <div key={id}>
              {renderWebinarContent(id, true, idx === 0)}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {uploadModal && (
          <ZoomUploadModal
            webinarId={uploadModal}
            webinarName={webinars.find((w) => w.id === uploadModal)?.name ?? uploadModal}
            onClose={() => setUploadModal(null)}
            onSuccess={() => handleUploadSuccess(uploadModal)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {addWebinarModal && (
          <AddWebinarModal
            onClose={() => setAddWebinarModal(false)}
            onSuccess={handleAddWebinarSuccess}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {stageLeadsModal && (
          <FunnelLeadsModal
            webinarId={stageLeadsModal.webinarId}
            webinarName={funnelCache[stageLeadsModal.webinarId]?.webinarName ?? stageLeadsModal.webinarId}
            stageLabel={stageLeadsModal.label}
            stageKey={stageLeadsModal.stageKey}
            traffic={stageLeadsModal.traffic}
            onClose={() => setStageLeadsModal(null)}
            onLeadClick={(email) => setTimelineEmail(email)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {timelineEmail && (
          <LeadTimelineDrawer
            email={timelineEmail}
            onClose={() => setTimelineEmail(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
